import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { User, PlanType, UserConfig, FaqItem, SystemSettings, EmailJsConfig, AddressItem, Appointment, GlobalPricing, BillingProfile, Payment, Document as AppDocument, DocumentType as AppDocumentType } from './types';
import { PLAN_LIMITS } from './src/constants/plans';
// import WidgetPreview from './components/WidgetPreview';
import { AdminCoupons } from './components/AdminCoupons';
// import AnalyticsDashboard from './components/AnalyticsDashboard'; // Imported
import InvoiceRequestModal from './components/InvoiceRequestModal';
import { AddMemberModal } from './components/AddMemberModal';
import PlanConfirmationModal from './components/PlanConfirmationModal';
import SubscriptionCancelModal from './components/SubscriptionCancelModal';
import { AiAssistant } from './components/AiAssistant'; // Import AI Chatbot
import FeatureGate from './components/FeatureGate';
import { ProfileForm } from './components/ProfileForm';
import { DocumentsManagementView } from './components/DocumentsManagementView';
import { ClientDocumentsView } from './components/ClientDocumentsView';
import { SystemSettingsView } from './components/SystemSettingsView';
import { BIFinancialView } from './components/BIFinancialView';
import { LanguageSwitcher } from './components/LanguageSwitcher'; // Imported
import { api } from './services/api';
// import { getGooglePlaceStats } from './services/geminiService';
import { formatCurrency, formatDate, formatDateTime } from './utils/formatters';
import { compressImage } from './utils/imageUtils';
import { PaymentModal } from './components/billing/PaymentModal';
import { BillingView } from './components/billing/BillingView';
import { AdminBillingView } from './components/billing/AdminBillingView';
import { FileBrowserView } from './components/FileBrowserView';
import { LoginView } from './components/auth/LoginView';
// import { AppointmentsView } from './components/appointments/AppointmentsView';
import ClientDashboard from './components/ClientDashboard'; // Imported Client Dashboard
import { MobileAppView } from './components/MobileAppView';
import { ClientObligationsView } from './components/ClientObligationsView';
import { ClientObligationsCalendar } from './components/ClientObligationsCalendar';
// Removed legacy firebaseConfig import to prevent 'firebase/app' conflicts
import {
    Settings,
    PieChart,
    CreditCard,
    Users,
    Code,
    Layout,
    Inbox,
    FileText,
    Save,
    Trash2,

    Plus,
    CheckCircle,
    Loader2,
    Palette,
    ExternalLink,
    Upload,
    Calendar,
    MoreVertical,
    Edit,
    UserPlus,
    Database,
    Check,
    Bot,
    Minus,
    X,
    LogOut,
    ShieldAlert,
    Lock,
    Key,
    Globe,
    Copy,
    ArrowRight,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    Mail,
    Shield,
    User as UserIcon,
    AlertCircle,
    AlertTriangle,
    RefreshCw,
    BarChart,
    TrendingUp,
    Tag,
    MousePointer,
    Share2,
    MapPin,
    Eye,
    AlignLeft,
    AlignCenter,
    AlignRight,
    MessageSquare,
    MessageCircle,
    PlayCircle,

    Type,
    Clock,
    ChevronLeft,
    ChevronRight,
    Send,
    CalendarCheck,
    Briefcase,
    Image,
    Link,
    DollarSign,
    Search,
    LayoutDashboard,
    FolderOpen,
    Smartphone,
    CalendarClock,
    HelpCircle
} from 'lucide-react';
import { auth, db } from './services/firebase';





interface AppProps {
    embedId?: string | null;
}

export function App({ embedId }: AppProps) {
    const { t, i18n } = useTranslation('dashboard');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [actingUser, setActingUser] = useState<User | null>(null);

    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [agencyClients, setAgencyClients] = useState<User[]>([]); // For Agency Dashboard
    const [agencyMembers, setAgencyMembers] = useState<User[]>([]); // For Agency Team
    const [selectedClient, setSelectedClient] = useState<User | null>(null); // For Agency Dashboard
    const [pendingDocsCount, setPendingDocsCount] = useState(0);
    const [unreadNotesCount, setUnreadNotesCount] = useState(0);
    const [documentsFilter, setDocumentsFilter] = useState<'unread_notes' | undefined>(undefined);
    // State
    // State
    const [view, setView] = useState<'config' | 'analytics' | 'embed' | 'admin' | 'admin_billing' | 'admin_coupons' | 'billing' | 'settings' | 'appointments' | 'agency' | 'documents' | 'browse_docs' | 'bi_financial' | 'client_dashboard' | 'mobile_app' | 'obligations'>('config');
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPlanDetails, setSelectedPlanDetails] = useState<{ id: PlanType, price: number, cycle: string } | null>(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [pendingUpgrade, setPendingUpgrade] = useState<{ plan: PlanType, price: number, cycle: string, taxRate: number } | null>(null);
    const [showCancelModal, setShowCancelModal] = useState(false);

    // AI Context Data
    const [aiDocuments, setAiDocuments] = useState<AppDocument[]>([]);
    const [aiDocTypes, setAiDocTypes] = useState<AppDocumentType[]>([]);

    // Admin User Modal
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newClientCompanyName, setNewClientCompanyName] = useState('');
    const [newClientResponsiblePerson, setNewClientResponsiblePerson] = useState('');
    const [newClientVat, setNewClientVat] = useState('');
    const [sendInvitation, setSendInvitation] = useState(true);
    const [newUserPlan, setNewUserPlan] = useState<PlanType>(PlanType.FREE);
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    // Agency Client Modal
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);

    // Resend Invitation Modal
    const [showResendInviteModal, setShowResendInviteModal] = useState(false);
    const [clientToResend, setClientToResend] = useState<User | null>(null);
    const [isResending, setIsResending] = useState(false);

    // Sync Language with User Config
    useEffect(() => {
        if (currentUser?.config?.language && i18n.language !== currentUser.config.language) {
            console.log("Syncing language from user config:", currentUser.config.language);
            i18n.changeLanguage(currentUser.config.language);
        }
    }, [currentUser?.config?.language]);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [clientSort, setClientSort] = useState<'name' | 'attention'>('name');
    const [isRecalculatingAttention, setIsRecalculatingAttention] = useState(false);
    const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
    const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
    const [clientToDelete, setClientToDelete] = useState<User | null>(null);
    const [createdClientCredentials, setCreatedClientCredentials] = useState<{ email: string, password: string } | null>(null);
    const [selectedAgencyFilter, setSelectedAgencyFilter] = useState<string>('all');

    const confirmDeleteClient = async () => {
        if (!clientToDelete || !currentUser) return;
        if (confirm("Are you sure? This cannot be undone.")) {
            try {
                await api.deleteAgencyClient(clientToDelete.id, currentUser.id);
                const all = await api.getAgencyClients(currentUser.id, undefined, true);
                setAgencyClients(all.filter(c => c.role !== 'agency_member'));
                setAgencyMembers(all.filter(c => c.role === 'agency_member'));
                setShowDeleteClientModal(false);
                setClientToDelete(null);
            } catch (e: any) {
                alert("Failed to delete client: " + e.message);
            }
        }
    };

    const handleRecalculateAttention = async () => {
        if (!currentUser) return;
        setIsRecalculatingAttention(true);
        try {
            await api.testRecalculateAttention();
            const all = await api.getAgencyClients(currentUser.id, undefined, true);
            setAgencyClients(all.filter(c => c.role !== 'agency_member'));
            alert("Client attention scores recalculated successfully.");
        } catch (e: any) {
            console.error(e);
            alert("Failed to recalculate attention scores: " + e.message);
        } finally {
            setIsRecalculatingAttention(false);
        }
    };

    const handleTestAttentionEmail = async () => {
        if (!currentUser) return;
        setIsSendingTestEmail(true);
        try {
            await api.testSendAttentionEmail();
            alert("Test attention email sent successfully. Check your inbox.");
        } catch (e: any) {
            console.error(e);
            alert("Failed to send test email: " + e.message);
        } finally {
            setIsSendingTestEmail(false);
        }
    };

    const [isEmbedMode, setIsEmbedMode] = useState(false);
    const [embedConfig, setEmbedConfig] = useState<UserConfig | null>(null);
    const [embedEmailConfig, setEmbedEmailConfig] = useState<EmailJsConfig | null>(null);
    const [embedStatus, setEmbedStatus] = useState<string>('loading');
    const [userFormErrors, setUserFormErrors] = useState<{ [key: string]: string }>({});
    const [settingsTab, setSettingsTab] = useState<'general' | 'team'>('general');

    // Upload States
    const [isLogoUploading, setIsLogoUploading] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [isSendingReport, setIsSendingReport] = useState(false);
    const [imageUrlInput, setImageUrlInput] = useState('');

    // Client Settings Modal (Agency View)
    const [showClientSettingsModal, setShowClientSettingsModal] = useState(false);
    const [selectedClientForSettings, setSelectedClientForSettings] = useState<User | null>(null);

    const isUploading = isLogoUploading || isImageUploading;

    const targetUser = actingUser || currentUser;

    const [quickStats, setQuickStats] = useState({ emails: 0, clicks: 0, opens: 0, appointments: 0, widgetClicks: 0, externalClicks: 0, widgetEmails: 0, externalEmails: 0, chatClicks: 0 });
    const [statsDateRange, setStatsDateRange] = useState<'all' | 'today' | 'yesterday' | 'week' | 'month' | 'year'>('all');

    useEffect(() => {
        if (currentUser?.language && currentUser.language !== i18n.language) {
            i18n.changeLanguage(currentUser.language);
        }
    }, [currentUser?.language]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlId = params.get('id');
        const urlMode = params.get('mode');

        const finalId = embedId || urlId;
        const finalMode = embedId ? 'embed' : urlMode;

        if (finalMode === 'embed' && finalId) {
            setIsEmbedMode(true);
            api.getPublicUserConfig(finalId).then((result) => {
                setEmbedStatus(result.status);
                if (result.config) {
                    setEmbedConfig(result.config);
                    if (result.emailJsConfig) {
                        setEmbedEmailConfig(result.emailJsConfig);
                    }
                }
            });
        }
    }, [embedId]);

    useEffect(() => {
        if (targetUser) {
            api.getAnalyticsEvents(targetUser.id).then(allEvents => {
                // Filter Events based on Date Range
                const now = new Date();
                const filteredEvents = allEvents.filter(e => {
                    if (statsDateRange === 'all') return true;

                    const eventDate = new Date(e.timestamp);
                    if (statsDateRange === 'today') {
                        return eventDate.toDateString() === now.toDateString();
                    }
                    if (statsDateRange === 'yesterday') {
                        const yest = new Date(now);
                        yest.setDate(yest.getDate() - 1);
                        return eventDate.toDateString() === yest.toDateString();
                    }
                    if (statsDateRange === 'week') {
                        const weekAgo = new Date(now);
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return eventDate >= weekAgo;
                    }
                    if (statsDateRange === 'month') {
                        const monthAgo = new Date(now);
                        monthAgo.setDate(monthAgo.getDate() - 30);
                        return eventDate >= monthAgo;
                    }
                    if (statsDateRange === 'year') {
                        const yearAgo = new Date(now);
                        yearAgo.setDate(yearAgo.getDate() - 365);
                        return eventDate >= yearAgo;
                    }
                    return true;
                });

                const events = filteredEvents;

                // Messages
                const widgetEmails = events.filter(e => e.eventType === 'email_form').length;
                const externalEmails = events.filter(e => e.eventType === 'external_link_click' && (e.details?.toLowerCase().startsWith('form submit') || e.details?.toLowerCase().includes('email click'))).length;
                const emails = widgetEmails + externalEmails;

                // Chat (Viber / Messenger / WhatsApp)
                const chatClicks = events.filter(e => e.eventType === 'phone_click' && (e.details?.toLowerCase().includes('viber') || e.details?.toLowerCase().includes('messenger') || e.details?.toLowerCase().includes('whatsapp'))).length;

                // Phone / Clicks (Exclude Chat)
                const widgetClicks = events.filter(e => e.eventType === 'phone_click' && !e.details?.toLowerCase().includes('viber') && !e.details?.toLowerCase().includes('messenger') && !e.details?.toLowerCase().includes('whatsapp')).length;
                const externalClicks = events.filter(e => e.eventType === 'external_link_click' && e.details?.toLowerCase().includes('phone')).length;
                const clicks = widgetClicks + externalClicks;

                const opens = events.filter(e => e.eventType === 'widget_open').length;
                const appointments = events.filter(e => e.eventType === 'booking_request').length;

                setQuickStats({ emails, clicks, opens, appointments, widgetClicks, externalClicks, widgetEmails, externalEmails, chatClicks });
            });
        }
    }, [targetUser, view, statsDateRange]);

    // Enforce View Permissions and Default Redirects
    useEffect(() => {
        if (currentUser && !actingUser) {
            // Client Restrictions
            if (currentUser.role !== 'admin' && currentUser.role !== 'agency' && currentUser.role !== 'agency_member') {
                const restrictedViews = ['config', 'analytics', 'appointments', 'embed'];
                // @ts-ignore
                if (restrictedViews.includes(view)) {
                    setView('bi_financial');
                }
            }
            // Agency Default
            else if (currentUser.role === 'agency' || currentUser.role === 'agency_member') {
                if (view === 'config') {
                    setView('agency');
                }
            }
            // Admin Default Redirect & Restrictions
            else if (currentUser.role === 'admin') {
                const restrictedForAdmin = ['config', 'analytics', 'appointments', 'embed', 'bi_financial', 'documents', 'client_dashboard'];
                // @ts-ignore
                if (restrictedForAdmin.includes(view)) {
                    setView('admin');
                }
            }
        }
    }, [currentUser, view]);

    useEffect(() => {
        if (currentUser?.role === 'agency' && !actingUser) {
            api.getAgencyClients(currentUser.id, undefined, true).then(all => {
                setAgencyClients(all.filter(c => c.role !== 'agency_member'));
                setAgencyMembers(all.filter(c => c.role === 'agency_member'));
            });
            if (view === 'config') setView('agency');
        } else if (currentUser?.role === 'agency_member' && !actingUser) {
            // Agency Member: Pass ID to filter
            api.getAgencyClients(currentUser.agencyId, currentUser.id).then(clients => {
                setAgencyClients(clients);
            });
            if (view === 'config') setView('agency');
        }
    }, [currentUser, actingUser]);



    // Fetch Pending Docs Count for Agency Dashboard
    useEffect(() => {
        if ((currentUser?.role === 'agency' || currentUser?.role === 'agency_member') && !actingUser) {
            const agencyId = currentUser.role === 'agency' ? currentUser.id : currentUser.agencyId;
            if (!agencyId) return;

            // For Agency: Fetch all docs. For Member: Fetch only assigned docs (but we need to know clients first).
            // Optimization: Members fetch clients first, then could fetch docs.
            // For Member: Fetch assigned clients, then their docs.
            // For Agency: Fetch all docs.

            let dataPromise;

            if (currentUser.role === 'agency_member') {
                // 1. Fetch assigned clients
                dataPromise = api.getAgencyClients(agencyId, currentUser.id).then(async (clients) => {
                    // 2. Fetch docs for each client
                    const docPromises = clients.map(c => api.getDocuments(agencyId, c.id));
                    const docsArrays = await Promise.all(docPromises);
                    const allDocs = docsArrays.flat();
                    return { docs: allDocs, clients };
                });
            } else {
                // Agency: Fetch all parallel
                dataPromise = Promise.all([
                    api.getDocuments(agencyId),
                    api.getAgencyClients(agencyId, undefined, true) // Fetch all including members
                ]).then(([docs, allUsers]) => ({ docs, clients: allUsers.filter(u => u.role !== 'agency_member') })); // Filter out members for client-specific doc counts
            }

            dataPromise.then(({ docs, clients }) => {
                let relevantDocs = docs;
                // Docs are already filtered/scoped by the fetch method above

                const pending = relevantDocs.filter(d => d.status === 'pending' && d.uploadedByRole === 'client').length;
                setPendingDocsCount(pending);

                const unread = relevantDocs.filter(d => d.metadata?.hasUnreadNotesForAgency).length;
                setUnreadNotesCount(unread);
            }).catch(console.error);
        }
    }, [currentUser, actingUser, view]);

    // Fetch Data for AI Assistant (Context)
    const refreshAiContext = useCallback(async () => {
        if (!targetUser) return;
        const isAiEnabled = targetUser.config?.ai_features?.document_ai_enabled;
        if (!isAiEnabled) return;

        try {
            const agencyId = targetUser.role === 'agency' ? targetUser.id : (targetUser.agencyId || targetUser.id);
            if (!agencyId) return;

            // 1. Fetch Document Types
            const types = await api.getDocumentTypes(agencyId, targetUser.role === 'user');
            setAiDocTypes(types);

            // 2. Fetch Documents (Scoped to Target User)
            const clientId = targetUser.role === 'user' ? targetUser.id : undefined;
            const docs = await api.getDocuments(agencyId, clientId);
            setAiDocuments(docs);
        } catch (e) {
            console.error("Failed to fetch AI context", e);
        }
    }, [targetUser]);

    useEffect(() => {
        refreshAiContext();
    }, [refreshAiContext]);

    // 4. Track External Links (Global Listener - Embed Mode Only)
    useEffect(() => {
        const effectiveId = embedId || new URLSearchParams(window.location.search).get('id');

        // Only run if:
        // 1. We are in Embed Mode
        // 2. Config is loaded
        // 3. Tracking is enabled
        // 4. We have a valid User ID to log to
        if (!isEmbedMode || !embedConfig || !embedConfig.trackExternalLinks || !effectiveId) return;

        const handleGlobalClick = (e: MouseEvent) => {
            const link = (e.target as HTMLElement).closest('a');
            if (link) {
                const href = link.href;
                if (href.startsWith('tel:')) {
                    api.logAnalyticsEvent(effectiveId, 'external_link_click', `Phone Click: ${href}`);
                } else if (href.startsWith('mailto:')) {
                    api.logAnalyticsEvent(effectiveId, 'external_link_click', `Email Click: ${href}`);
                }
            }
        };

        const handleGlobalSubmit = (e: Event) => {
            const form = e.target as HTMLFormElement;
            if (form) {
                const formName = form.getAttribute('name') || form.getAttribute('id') || 'Unknown Form';
                const action = form.getAttribute('action') || 'No Action';
                api.logAnalyticsEvent(effectiveId, 'external_link_click', `Form Submit: ${formName} (${action})`);
            }
        };

        document.addEventListener('click', handleGlobalClick, true);
        document.addEventListener('submit', handleGlobalSubmit, true);

        return () => {
            document.removeEventListener('click', handleGlobalClick, true);
            document.removeEventListener('submit', handleGlobalSubmit, true);
        };
    }, [isEmbedMode, embedConfig, embedId]);

    if (isEmbedMode) {
        if (embedStatus === 'loading') return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-slate-400" /></div>;

        if (embedStatus !== 'active') {
            return (
                <div className="fixed bottom-6 right-6 z-50 animate-fadeIn">
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl shadow-lg text-sm font-bold border border-red-100 flex items-center gap-2">
                        <AlertTriangle size={18} />
                        {embedStatus === 'expired' ? 'Subscription Expired' : 'Widget Unavailable'}
                    </div>
                </div>
            );
        }

        if (!embedConfig) return null;

        const finalId = embedId || new URLSearchParams(window.location.search).get('id') || undefined;

        return (
            <div className="w-full h-full bg-transparent font-sans text-slate-900">
                {/* <WidgetPreview config={embedConfig} isEmbedded={true} emailJsConfig={embedEmailConfig} userId={finalId} /> */}
            </div>
        );
    }



    const handleLogin = async (email: string, password: string): Promise<void> => {
        const user = await api.login(email, password);
        setCurrentUser(user);
        if (user.config?.language) {
            i18n.changeLanguage(user.config.language);
        }
        if (user?.role === 'admin') { const usersList = await api.getAllUsers(); setAllUsers(usersList); }
        if (user.role === 'agency') {
            const clients = await api.getAgencyClients(user.id, undefined, true); // Fetch all including members for filtering
            setAgencyClients(clients.filter(c => c.role !== 'agency_member'));
            setAgencyMembers(clients.filter(c => c.role === 'agency_member'));
        } else if (user.role === 'agency_member' && user.agencyId) {
            api.getAgencyClients(user.agencyId, user.id).then(setAgencyClients);
        }
        setView((user.role === 'agency' || user.role === 'agency_member') ? 'agency' : (user.role === 'admin' ? 'admin' : 'client_dashboard'));
    };

    const handleGoogleLogin = async (): Promise<void> => {
        const user = await api.loginWithGoogle();
        setCurrentUser(user);
        if (user.config?.language) {
            i18n.changeLanguage(user.config.language);
        }
        if (user?.role === 'admin') { const usersList = await api.getAllUsers(); setAllUsers(usersList); }
        if (user.role === 'agency') {
            const clients = await api.getAgencyClients(user.id, undefined, true); // Fetch all including members for filtering
            setAgencyClients(clients.filter(c => c.role !== 'agency_member'));
            setAgencyMembers(clients.filter(c => c.role === 'agency_member'));
        } else if (user.role === 'agency_member' && user.agencyId) {
            api.getAgencyClients(user.agencyId, user.id).then(setAgencyClients);
        }
        setView((user.role === 'agency' || user.role === 'agency_member') ? 'agency' : (user.role === 'admin' ? 'admin' : 'client_dashboard'));
    };

    const handleRegister = async (email: string, password: string, language: string = 'en', companyName: string = 'My Company'): Promise<void> => {
        const user = await api.register(email, password, language, companyName);
        // Ensure emailVerified is false for new registrations to trigger the gate
        setCurrentUser({ ...user, emailVerified: false });
        setView('billing');
    };
    const handleResetPassword = async (email: string): Promise<void> => { await api.sendPasswordResetLink(email); };

    const handleLogout = () => {
        setCurrentUser(null);
        setActingUser(null);
        setAllUsers([]);
        setAgencyClients([]);
        setAgencyMembers([]);
    };

    const updateConfig = (key: keyof UserConfig, value: any) => {
        if (targetUser) {
            const updatedUser = { ...targetUser, config: { ...targetUser.config, [key]: value } };
            if (actingUser) {
                setActingUser(updatedUser);
                setAgencyClients(prev => prev.map(c => c.id === updatedUser.id ? updatedUser : c));
            } else {
                setCurrentUser(updatedUser);
            }
        }
    };

    const handleSaveChanges = async () => {
        if (!targetUser) return;
        if (isUploading) {
            alert("Please wait for file processing to finish.");
            return;
        }
        setIsSaving(true);
        try {
            await api.updateUserConfig(targetUser.id, targetUser.config);
        } catch (e: any) {
            console.error("Save failed", e);
            alert(`Failed to save changes: ${e.message}. Please ensure your internet connection is stable.`);
        } finally {
            setIsSaving(false);
        }
    };

    const addFaq = () => { if (targetUser) updateConfig('faqs', [...targetUser.config.faqs, { id: crypto.randomUUID(), question: 'New Question', answer: 'New Answer', isVisible: true, source: 'manual' }]); };
    const updateFaq = (id: string, field: keyof FaqItem, value: any) => { if (targetUser) updateConfig('faqs', targetUser.config.faqs.map(f => f.id === id ? { ...f, [field]: value } : f)); };

    const moveFaq = (index: number, direction: 'up' | 'down') => {
        if (!targetUser) return;
        const newFaqs = [...targetUser.config.faqs];
        if (direction === 'up' && index > 0) {
            [newFaqs[index], newFaqs[index - 1]] = [newFaqs[index - 1], newFaqs[index]];
        } else if (direction === 'down' && index < newFaqs.length - 1) {
            [newFaqs[index], newFaqs[index + 1]] = [newFaqs[index + 1], newFaqs[index]];
        }
        updateConfig('faqs', newFaqs);
    };
    const removeFaq = (id: string) => { if (targetUser) updateConfig('faqs', targetUser.config.faqs.filter(f => f.id !== id)); };

    const addAddress = () => {
        if (targetUser && (targetUser.config.addresses || []).length < 3) {
            const newAddr: AddressItem = { id: crypto.randomUUID(), label: 'New Location', address: '123 Main St, City', mapUrl: '' };
            updateConfig('addresses', [...(targetUser.config.addresses || []), newAddr]);
        } else {
            alert("Maximum 3 addresses allowed.");
        }
    };
    const updateAddress = (id: string, field: keyof AddressItem, value: any) => {
        if (targetUser) {
            updateConfig('addresses', (targetUser.config.addresses || []).map(a => a.id === id ? { ...a, [field]: value } : a));
        }
    };
    const removeAddress = (id: string) => {
        if (targetUser) {
            updateConfig('addresses', (targetUser.config.addresses || []).filter(a => a.id !== id));
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && targetUser) {
            setIsLogoUploading(true);
            try {
                const base64String = await compressImage(file, 300, 0.7);
                updateConfig('logoUrl', base64String);
            } catch (error: any) {
                console.error("Logo processing failed", error);
                alert("Failed to process logo.");
            } finally {
                setIsLogoUploading(false);
            }
        }
    };

    const handleWelcomeImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0 && targetUser) {
            setIsImageUploading(true);
            try {
                const newImages = [...(targetUser.config.welcomeImages || [])];
                if (newImages.length + files.length > 5) {
                    alert("Maximum 5 images allowed.");
                    setIsImageUploading(false);
                    return;
                }

                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const base64String = await compressImage(file, 600, 0.6);
                    newImages.push({ url: base64String, link: '' });
                }
                updateConfig('welcomeImages', newImages);
            } catch (error: any) {
                console.error("Image processing failed", error);
                alert("Failed to process image.");
            } finally {
                setIsImageUploading(false);
            }
        }
    };

    const handleAddImageUrl = () => {
        if (imageUrlInput && targetUser) {
            const newImages = [...(targetUser.config.welcomeImages || [])];
            if (newImages.length >= 5) {
                alert("Maximum 5 images allowed.");
                return;
            }
            newImages.push({ url: imageUrlInput, link: '' });
            updateConfig('welcomeImages', newImages);
            setImageUrlInput('');
        }
    };

    const updateWelcomeImageLink = (index: number, link: string) => {
        if (targetUser && targetUser.config.welcomeImages) {
            const newImages = [...targetUser.config.welcomeImages];
            if (newImages[index]) {
                newImages[index].link = link;
                updateConfig('welcomeImages', newImages);
            }
        }
    };

    const removeWelcomeImage = (index: number) => {
        if (targetUser && targetUser.config.welcomeImages) {
            const newImages = targetUser.config.welcomeImages.filter((_, i) => i !== index);
            updateConfig('welcomeImages', newImages);
        }
    };

    const initUpgrade = (plan: PlanType, price: number, cycle: string, taxRate: number) => {
        setPendingUpgrade({ plan, price, cycle, taxRate });
    };

    const handleConfirmUpgrade = async (couponCode?: string) => {
        if (!pendingUpgrade) return;
        const { plan, price, cycle } = pendingUpgrade;

        setIsProcessingPayment(true);
        const validInterval = cycle === 'monthly' ? 'month' : (cycle === 'yearly' ? 'year' : cycle);

        try {
            // Fetch latest user data
            let target = currentUser;
            try {
                if (currentUser?.id) target = await api.getUser(currentUser.id);
            } catch (e) {
                console.warn("Used cached user data due to fetch error", e);
            }

            // Fallback: Create new checkout session with potential coupon
            const { url } = await api.createCheckoutSession(plan, validInterval, couponCode);
            window.location.href = url;

        } catch (error: any) {
            console.error("Payment Error:", error);
            alert("Failed to process request: " + error.message);
            setIsProcessingPayment(false); // Only stop loading on error, otherwise we might be redirecting
        }
    };

    // processPayment is bypassed by the new flow
    const processPayment = async () => { };

    const handleCreateUser = async () => { if (!newUserEmail) return; setIsCreatingUser(true); await api.createUser(newUserEmail, newUserPlan); setAllUsers(await api.getAllUsers()); setShowAddUserModal(false); setNewUserEmail(''); setIsCreatingUser(false); };
    const handleDeleteUser = async (uid: string) => { if (confirm("Delete?")) { await api.deleteUser(uid); setAllUsers(await api.getAllUsers()); } };
    const handleExtendSubscription = async (uid: string) => { const m = prompt("Months:", "1"); if (m) { await api.extendSubscription(uid, parseInt(m)); setAllUsers(await api.getAllUsers()); } };
    const handleChangePlan = async (uid: string, plan: PlanType) => { await api.updateSubscription(uid, plan); setAllUsers(await api.getAllUsers()); };
    const handleStatusChange = async (uid: string, status: 'active' | 'expired' | 'cancelled') => { await api.updateUserStatus(uid, status); setAllUsers(await api.getAllUsers()); };

    const handleCreateClient = async () => {
        const limit = PLAN_LIMITS[currentUser.plan]?.clients || 0;
        if (agencyClients.length >= limit) {
            alert(`You have reached your plan limit of ${limit} clients. Please upgrade to add more.`);
            return;
        }
        if (!currentUser || currentUser.role !== 'agency') return;
        if (!newUserEmail) return;
        setIsCreatingUser(true);
        try {
            const { user, password } = await api.createAgencyClient(currentUser.id, newUserEmail, newClientCompanyName, newClientResponsiblePerson, newClientVat, sendInvitation);
            const all = await api.getAgencyClients(currentUser.id, undefined, true);
            setAgencyClients(all.filter(c => c.role !== 'agency_member'));
            setAgencyMembers(all.filter(c => c.role === 'agency_member'));
            setShowAddClientModal(false);
            setNewUserEmail('');
            setNewClientCompanyName('');
            setNewUserEmail('');
            setNewClientCompanyName('');
            setNewClientResponsiblePerson('');
            setNewClientVat('');
            setCreatedClientCredentials({ email: user.email, password });
        } catch (e: any) {
            console.error(e);
            const errorMessage = e.message?.toLowerCase() || '';
            const errorCode = e.code || '';

            if (errorMessage.includes('email-already-in-use') ||
                errorMessage.includes('already in use') ||
                errorCode === 'auth/email-already-in-use') {

                if (confirm("This email is already registered in the system (Authentication) but not found in your client list.\n\nIt is likely an 'orphaned' account from a previous deletion.\n\nDo you want to FORCE CLEAN this email so you can create the client?")) {
                    try {
                        await api.forceDeleteUserByEmail(newUserEmail);
                        alert("Account cleaned! Retrying creation...");
                        // Retry creation
                        const { user, password } = await api.createAgencyClient(currentUser.id, newUserEmail, newClientCompanyName, newClientResponsiblePerson, newClientVat, sendInvitation);
                        const all = await api.getAgencyClients(currentUser.id, undefined, true);
                        setAgencyClients(all.filter(c => c.role !== 'agency_member'));
                        setAgencyMembers(all.filter(c => c.role === 'agency_member'));
                        setShowAddClientModal(false);
                        setNewUserEmail('');
                        setNewClientCompanyName('');
                        setNewClientResponsiblePerson('');
                        setNewClientVat('');
                        setCreatedClientCredentials({ email: user.email, password });
                        return; // Success on retry
                    } catch (retryError: any) {
                        alert("Failed to recover: " + retryError.message);
                    }
                }
            } else {
                alert(e.message);
            }
        } finally {
            setIsCreatingUser(false);
        }
    };

    const handleCreateMember = async (data: { email: string, name: string, phone: string, password?: string }) => {
        if (!currentUser || currentUser.role !== 'agency') {
            alert("Only agency owners can add team members.");
            return false;
        }
        const limit = PLAN_LIMITS[currentUser.plan]?.members || 0;
        if (agencyMembers.length >= limit) {
            alert(`You have reached your plan limit of ${limit} team members. Please upgrade to add more.`);
            return false;
        }
        try {
            await api.createAgencyMember(currentUser.id, data);
            // Refresh
            const all = await api.getAgencyClients(currentUser.id, undefined, true);
            setAgencyClients(all.filter(c => c.role !== 'agency_member'));
            setAgencyMembers(all.filter(c => c.role === 'agency_member'));
            return true;
        } catch (e: any) {
            alert(e.message);
            return false;
        }
    };

    const handleDeleteMember = async (memberId: string) => {
        if (!currentUser || currentUser.role !== 'agency') {
            alert("Only agency owners can delete team members.");
            return;
        }
        if (!window.confirm('Are you sure you want to delete this team member? This action cannot be undone.')) return;
        try {
            await api.deleteAgencyMember(memberId, currentUser.id);
            // Refresh
            const all = await api.getAgencyClients(currentUser.id, undefined, true);
            setAgencyClients(all.filter(c => c.role !== 'agency_member'));
            setAgencyMembers(all.filter(c => c.role === 'agency_member'));
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleToggleClientStatus = async (client: User) => {
        const newStatus = client.subscriptionStatus === 'active' ? 'inactive' : 'active';
        // Optimistic update
        setAgencyClients(prev => prev.map(c => c.id === client.id ? { ...c, subscriptionStatus: newStatus as any } : c));
        try {
            await api.updateUserStatus(client.id, newStatus as any);
        } catch (e: any) {
            alert("Failed to update status");
            // Revert
            setAgencyClients(prev => prev.map(c => c.id === client.id ? { ...c, subscriptionStatus: client.subscriptionStatus } : c));
        }
    };



    const handleToggleClientFeature = async (client: User | null, featureKey: string, isNestedAiFeature: boolean = false) => {
        if (!client) return;

        // Optimistic Update
        const targetConfig = (client.config || {}) as UserConfig;
        const oldVal = isNestedAiFeature
            ? targetConfig.ai_features?.[featureKey as keyof typeof targetConfig.ai_features]
            : targetConfig[featureKey as keyof UserConfig];

        const newVal = !oldVal;

        // Create Payload
        let updatePayload: any = {};
        if (isNestedAiFeature) {
            updatePayload = {
                [`config.ai_features.${featureKey}`]: newVal
            };
        } else {
            updatePayload = {
                [`config.${featureKey}`]: newVal
            };
        }

        // Apply Optimistic Update to Local State
        setAgencyClients(prev => prev.map(c => {
            if (c.id === client.id) {
                // Deep merge for nested
                if (isNestedAiFeature) {
                    return {
                        ...c,
                        config: {
                            ...c.config,
                            ai_features: {
                                ...c.config.ai_features,
                                [featureKey]: newVal
                            }
                        }
                    };
                } else {
                    return {
                        ...c,
                        config: {
                            ...c.config,
                            [featureKey]: newVal
                        }
                    };
                }
            }
            return c;
        }));

        // Update Selected Client if open
        if (selectedClientForSettings && selectedClientForSettings.id === client.id) {
            setSelectedClientForSettings(prev => {
                if (!prev) return null;
                if (isNestedAiFeature) {
                    return { ...prev, config: { ...prev.config, ai_features: { ...prev.config.ai_features, [featureKey]: newVal } } };
                }
                return { ...prev, config: { ...prev.config, [featureKey]: newVal } };
            });
        }

        try {
            await api.updateUser(client.id, updatePayload);
        } catch (e) {
            console.error("Failed to toggle feature", e);
            alert("Failed to update setting");
            // Revert logic omitted for brevity (ideal: refetch client)
            setAgencyClients(await api.getAgencyClients(currentUser.id)); // Hard Revert
        }
    };

    const handleResendInvitation = (client: User) => {
        setClientToResend(client);
        setShowResendInviteModal(true);
    };

    const confirmResendInvitation = async () => {
        if (!clientToResend) return;
        setIsResending(true);
        try {
            await api.resendInvitation(clientToResend.id);
            alert(t('agency.modals.resend_invitation.success', 'Invitation sent successfully!'));
            setShowResendInviteModal(false);
            setClientToResend(null);
        } catch (e: any) {
            console.error("Failed to resend invitation", e);
            alert(t('agency.modals.resend_invitation.error', 'Failed to send invitation: ') + e.message);
        } finally {
            setIsResending(false);
        }
    };

    const handleManageClient = (client: User) => {
        setActingUser(client);
        setView('client_dashboard');
    };

    const handleExitClient = () => {
        setActingUser(null);
        if (currentUser?.role === 'admin') {
            setView('admin');
        } else if (currentUser?.role === 'agency' || currentUser?.role === 'agency_member') {
            setView('agency');
        } else {
            setView('client_dashboard'); // Default for clients
        }
    };

    const handleSyncGoogle = async () => {
        if (!targetUser) return;
        setIsSyncing(true);
        try {
            const settings = await api.getSystemSettings();
            if (!settings.aiApiKey) {
                alert("Action Required: Please enter your Google Gemini API Key in 'System Settings' to use this feature.");
                setIsSyncing(false);
                return;
            }

            // const stats = await getGooglePlaceStats(
            //     targetUser.config.googlePlaceId,
            //     targetUser.config.companyName,
            //     settings.aiApiKey
            // );

            // updateConfig('googleRating', stats.rating);
            // updateConfig('googleReviewCount', stats.count);
            // alert(`Synced successfully! Found ${stats.rating} stars and ${stats.count} reviews.`);
        } catch (e: any) {
            alert(`Sync Failed: ${e.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleSendTestReport = async () => {
        if (!targetUser || !targetUser.config.contactEmail) {
            alert("Please configure a Contact Email first.");
            return;
        }

        setIsSendingReport(true);
        try {
            // Determine date range based on frequency
            const frequency = targetUser.config.reportFrequency || 'weekly';
            const endDate = new Date();
            const startDate = new Date();
            let dateRangeLabel = '';

            if (frequency === 'daily') {
                startDate.setDate(endDate.getDate() - 1); // Last 24 hours
                dateRangeLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (Last 24h)`;
            } else if (frequency === 'weekly') {
                startDate.setDate(endDate.getDate() - 7); // Last 7 days
                dateRangeLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (Last 7 Days)`;
            } else {
                startDate.setDate(endDate.getDate() - 30); // Monthly/Default
                dateRangeLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            }

            // Fetch specific events for this range
            const events = await api.getAnalyticsEvents(targetUser.id, startDate, endDate);

            // Calculate Stats (Replica of Server Logic)
            const widgetEmails = events.filter(e => e.eventType === 'email_form').length;
            const externalEmails = events.filter(e => e.eventType === 'external_link_click' && (e.details?.toLowerCase().startsWith('form submit') || e.details?.toLowerCase().includes('email click'))).length;
            const emails = widgetEmails + externalEmails;

            const chatClicks = events.filter(e => e.eventType === 'phone_click' && (e.details?.toLowerCase().includes('viber') || e.details?.toLowerCase().includes('messenger') || e.details?.toLowerCase().includes('whatsapp'))).length;

            const widgetClicks = events.filter(e => e.eventType === 'phone_click' && !e.details?.toLowerCase().includes('viber') && !e.details?.toLowerCase().includes('messenger') && !e.details?.toLowerCase().includes('whatsapp')).length;
            const externalClicks = events.filter(e => e.eventType === 'external_link_click' && e.details?.toLowerCase().includes('phone')).length;
            const clicks = widgetClicks + externalClicks;

            const opens = events.filter(e => e.eventType === 'widget_open').length;
            const appointments = events.filter(e => e.eventType === 'booking_request').length;

            const scopeStats = {
                opens,
                appointments,
                emails, widgetEmails, externalEmails,
                clicks, widgetClicks, externalClicks, chatClicks
            };

            await api.sendTestReport(
                targetUser.config.contactEmail,
                targetUser.config.companyName,
                scopeStats,
                dateRangeLabel // Pass valid range label
            );
            alert(`Test ${frequency} report sent to ${targetUser.config.contactEmail}`);
        } catch (e: any) {
            console.error(e);
            alert(`Failed to send report: ${e.message}`);
        } finally {
            setIsSendingReport(false);
        }
    };

    const handleCancelSubscription = () => {
        setShowCancelModal(true);
    };

    const handleConfirmCancel = async () => {
        if (!targetUser) return;
        setIsProcessingPayment(true);
        try {
            await api.cancelSubscription();
            // Stripe keeps status 'active' until period end, but sets cancel_at_period_end=true.
            // We update local state to reflect this immediate intent.
            const updatedUser: User = {
                ...targetUser,
                cancel_at_period_end: true
            };
            if (actingUser) {
                setActingUser(updatedUser);
            } else {
                setCurrentUser(updatedUser);
            }
            setShowCancelModal(false);
        } catch (e: any) {
            alert("Failed to cancel subscription: " + e.message);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const toggleAvailableDay = (dayIndex: number) => {
        if (!targetUser) return;
        const currentDays = targetUser.config.availableDays || [1, 2, 3, 4, 5];
        let newDays;
        if (currentDays.includes(dayIndex)) {
            newDays = currentDays.filter(d => d !== dayIndex);
        } else {
            newDays = [...currentDays, dayIndex].sort();
        }
        updateConfig('availableDays', newDays);
    };

    const weekDays = [
        { id: 1, label: 'Mon' },
        { id: 2, label: 'Tue' },
        { id: 3, label: 'Wed' },
        { id: 4, label: 'Thu' },
        { id: 5, label: 'Fri' },
        { id: 6, label: 'Sat' },
        { id: 0, label: 'Sun' },
    ];


    // DEBUG: Monitor Auth State
    if (currentUser) {
        console.warn("DEBUG: Checking Verification. Verified:", currentUser.emailVerified);
        if (currentUser.emailVerified !== true) {
            console.warn("DEBUG: User SHOULD be blocked.");
        }
    }

    // Ensure Verification (unless verified or specific role/test)
    if (currentUser && currentUser.emailVerified !== true) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white max-w-md w-full p-8 rounded-2xl border border-slate-200 shadow-sm text-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Mail className="text-blue-600" size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('dashboard:verification.title')}</h2>
                    <p className="text-slate-600 mb-6">
                        <Trans
                            i18nKey="dashboard:verification.message"
                            values={{ email: currentUser.email }}
                            components={{ strong: <strong /> }}
                        />
                    </p>

                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition mb-3"
                    >
                        {t('dashboard:verification.button_refresh')}
                    </button>

                    <button
                        onClick={() => {
                            setCurrentUser(null);
                            setActingUser(null);
                        }}
                        className="text-slate-500 font-semibold hover:text-slate-700"
                    >
                        {t('dashboard:verification.button_login')}
                    </button>
                </div>
            </div>
        );
    }
    if (!currentUser) {
        return <LoginView onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} onRegister={handleRegister} onResetPassword={handleResetPassword} />;
    }

    // Safety guard for missing config (prevents WSOD)
    if (!targetUser || !targetUser.config) {
        // If it's an admin, we can allow them to proceed with default/null config to avoid lockout
        if (currentUser && currentUser.role === 'admin') {
            // Admin can proceed - we might mock the config or rely on the UI checking for its existence
            // The App uses targetUser.config liberally so we should patch it if missing.
            if (targetUser && !targetUser.config) {
                // @ts-ignore
                targetUser.config = {
                    language: 'en',
                    companyName: 'System Admin',
                    faqs: [],
                    addresses: [],
                    welcomeImages: [],
                    availableDays: [],
                    phone1: '', phone1Label: '', phone2: '', phone2Label: '', phone3: '', phone3Label: '', mobile: '', mobileLabel: '',
                    googlePlaceId: '', googleRating: 0, googleReviewCount: 0,
                    themeColor: '#000000'
                };
            }
        } else {
            return (
                <div className="min-h-screen flex items-center justify-center bg-slate-50">
                    <div className="text-center">
                        <Loader2 size={40} className="animate-spin text-blue-600 mb-4 mx-auto" />
                        <h2 className="text-lg font-bold text-slate-800">Loading Configuration...</h2>
                        <p className="text-slate-500 text-sm mt-2">Please wait while we prepare your dashboard.</p>
                        <button onClick={() => { setCurrentUser(null); window.location.reload(); }} className="mt-6 text-sm text-red-500 hover:underline">Force Logout</button>
                        {/* Debug Info */}
                        <div className="mt-8 p-4 bg-slate-100 rounded text-xs font-mono text-left max-w-sm mx-auto overflow-auto max-h-40 border border-slate-200">
                            UID: {currentUser.id}<br />
                            Role: {currentUser.role}<br />
                            HasConfig: {currentUser.config ? 'Yes' : 'No'}
                        </div>
                    </div>
                </div>
            );
        }
    }

    const SidebarItem = ({ icon: Icon, label, active, onClick }: any) => (
        <button
            onClick={onClick}
            title={!isSidebarOpen ? label : ''}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'} ${!isSidebarOpen ? 'justify-center' : ''}`}
        >
            <Icon size={20} className="shrink-0" />
            {isSidebarOpen && <span className="font-medium whitespace-nowrap">{label}</span>}
        </button>
    );


    if (view === 'mobile_app') {

        const handleBack = () => setView('client_dashboard');
        return (
            <div className="flex h-screen bg-slate-50 overflow-hidden font-sans text-slate-900">
                <main className="flex-1 overflow-y-auto w-full">
                    {/* Header */}
                    <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-lg border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={handleBack} className="p-2 hover:bg-slate-100 rounded-lg transition text-slate-500">
                                <ArrowLeft size={24} />
                            </button>
                            <h1 className="text-xl font-bold">{t('mobile_app.title')}</h1>
                        </div>
                        {/* User Profile Dropdown / Actions */}
                        <div className="flex items-center gap-3">
                            <LanguageSwitcher
                                onLanguageChange={(lang) => updateConfig('language', lang)}
                            />
                        </div>
                    </div>

                    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
                        <MobileAppView onBack={handleBack} />
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900">

            <div className={`bg-white border-r border-slate-200 p-6 flex flex-col fixed h-full z-10 transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
                <div className={`flex items-center mb-10 ${isSidebarOpen ? 'justify-between' : 'justify-center flex-col gap-4'}`}>
                    {isSidebarOpen ? (
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center text-white font-bold shrink-0"><img src="/logo-b.svg" width="20" alt="Accounting Flow Logo" /></div>
                            <span className="text-xl font-bold text-slate-800 truncate">AccountingFLow</span>
                        </div>
                    ) : (
                        <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center text-white font-bold shrink-0"><img src="/logo-b.svg" width="20" alt="Accounting Flow Logo" /></div>
                    )}
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"
                    >
                        {isSidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                    </button>
                </div>

                <nav className="space-y-1 flex-1 overflow-y-auto overflow-x-hidden no-scrollbar">
                    {/* User Dashboard - Hide for Admin */}
                    {currentUser.role !== 'agency' && currentUser.role !== 'agency_member' && currentUser.role !== 'admin' && (
                        <SidebarItem
                            icon={LayoutDashboard}
                            label="Dashboard"
                            active={view === 'client_dashboard'}
                            onClick={() => setView('client_dashboard')}
                        />
                    )}

                    {(currentUser.role === 'agency' || currentUser.role === 'agency_member') && !actingUser && (
                        <>
                            <SidebarItem icon={Briefcase} label={t('sidebar.agency')} active={view === 'agency'} onClick={() => setView('agency')} />
                            <SidebarItem icon={FileText} label={t('sidebar.documents')} active={view === 'documents'} onClick={() => { setDocumentsFilter(undefined); setView('documents'); }} />
                            <SidebarItem icon={FolderOpen} label="Browse Docs" active={view === 'browse_docs'} onClick={() => setView('browse_docs')} />
                            {currentUser.role === 'agency' && (
                                <SidebarItem icon={CreditCard} label={t('sidebar.billing')} active={view === 'billing'} onClick={() => setView('billing')} />
                            )}
                            <SidebarItem icon={Settings} label={t('sidebar.settings')} active={view === 'settings'} onClick={() => setView('settings')} />
                        </>
                    )}

                    {((currentUser.role !== 'agency' && currentUser.role !== 'agency_member') || actingUser) && (
                        <>
                            {/* Admin specific items were here (removed as requested) */}

                            {/* Hide BI & Documents from Admin */}
                            {currentUser.role !== 'admin' && (
                                <>
                                    <SidebarItem icon={FileText} label={t('sidebar.my_documents')} active={view === 'documents'} onClick={() => { setDocumentsFilter(undefined); setView('documents'); }} />
                                    <SidebarItem icon={FolderOpen} label="Browse Docs" active={view === 'browse_docs'} onClick={() => setView('browse_docs')} />
                                    <SidebarItem icon={CalendarClock} label={t('sidebar.obligations')} active={view === 'obligations'} onClick={() => setView('obligations')} />
                                </>
                            )}

                            {/* Billing & Plans - Visible to Admin & User */}
                            {!actingUser && (currentUser.role === 'admin' || (currentUser.role === 'user' && !currentUser.agencyId)) && (
                                <SidebarItem icon={CreditCard} label={t('sidebar.billing_plans')} active={view === 'billing'} onClick={() => setView('billing')} />
                            )}

                            {(currentUser.role === 'user' || actingUser) && <SidebarItem icon={Settings} label={t('sidebar.settings')} active={view === 'settings'} onClick={() => setView('settings')} />}
                        </>
                    )}

                    {currentUser.role === 'admin' && (
                        <div className="mt-8">
                            {isSidebarOpen && <div className="px-4 text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('sidebar.admin_header')}</div>}
                            {!isSidebarOpen && <div className="h-4"></div>}
                            <SidebarItem icon={Users} label={t('sidebar.admin_users')} active={view === 'admin'} onClick={() => setView('admin')} />
                            <SidebarItem icon={Tag} label={t('sidebar.admin_coupons')} active={view === 'admin_coupons'} onClick={() => setView('admin_coupons')} />
                            <SidebarItem icon={CreditCard} label={t('sidebar.admin_billing')} active={view === 'admin_billing'} onClick={() => setView('admin_billing')} />
                            <SidebarItem icon={Settings} label={t('sidebar.system_settings')} active={view === 'settings'} onClick={() => setView('settings')} />
                        </div>
                    )}
                </nav>

                <div className="pt-6 border-t border-slate-100">
                    {currentUser.role !== 'agency_member' && currentUser.role !== 'user' && (
                        isSidebarOpen ? (
                            <div className="p-4 bg-slate-50 rounded-xl mb-4">
                                <p className="text-xs text-slate-500 font-medium uppercase mb-1">Current Plan</p>
                                <p className="text-lg font-bold text-slate-900 capitalize">{t(`plans.${currentUser.plan}.name`, { ns: 'billing', defaultValue: currentUser.plan })}</p>
                                {currentUser.plan !== PlanType.ADVANCED && currentUser.plan !== PlanType.AGENCY && <button onClick={() => setView('billing')} className="mt-3 w-full py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700">Upgrade</button>}
                            </div>
                        ) : (
                            <div className="flex justify-center mb-4">
                                <button onClick={() => setView('billing')} title="Upgrade Plan" className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        )
                    )}

                    {currentUser.role === 'user' && (isSidebarOpen ? (
                        <div
                            onClick={() => setView('mobile_app')}
                            className="rounded-xl p-4 mb-4 cursor-pointer hover:shadow-lg transition group relative overflow-hidden"
                            style={{ backgroundColor: '#2C313B' }}
                        >
                            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:scale-110 transition duration-500">
                                <Smartphone size={64} style={{ color: '#FFD810' }} className="transform rotate-12" />
                            </div>
                            <div className="relative z-10 flex items-center gap-3">
                                <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
                                    <Smartphone style={{ color: '#FFD810' }} size={24} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-white font-bold text-sm leading-tight">
                                        {t('mobile_app.banner_text')}
                                    </p>
                                    <p className="text-slate-300 text-[10px] mt-1 font-medium flex items-center gap-1 group-hover:translate-x-1 transition">
                                        {t('agency.stats.new_docs').split(' ')[0]} <ArrowRight size={10} style={{ color: '#FFD810' }} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center mb-4">
                            <button
                                onClick={() => setView('mobile_app')}
                                title={t('mobile_app.banner_text')}
                                className="w-10 h-10 rounded-xl text-white flex items-center justify-center shadow-md hover:shadow-lg transition hover:scale-105"
                                style={{ backgroundColor: '#2C313B', color: '#FFD810' }}
                            >
                                <Smartphone size={20} />
                            </button>
                        </div>
                    ))}

                    <div className="mb-4">
                        {/* Help Desk Button for Agency - Added above Language Selector */}
                        {currentUser.role === 'agency' && (
                            <button
                                onClick={() => window.open('https://accountingflow.eu/help-desk-accounting-firms/', '_blank')}
                                title={!isSidebarOpen ? "Help Desk" : ''}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-slate-600 hover:bg-slate-50 mb-2 ${!isSidebarOpen ? 'justify-center' : ''}`}
                            >
                                <HelpCircle size={20} className="shrink-0" />
                                {isSidebarOpen && <span className="font-medium whitespace-nowrap">Help Desk</span>}
                            </button>
                        )}

                        <LanguageSwitcher
                            collapsed={!isSidebarOpen}
                            onLanguageChange={(lang) => updateConfig('language', lang)}
                        />
                    </div>

                    <div className={`flex items-center ${isSidebarOpen ? 'justify-between' : 'justify-center flex-col gap-3'}`}>
                        {isSidebarOpen ? (
                            <div className="flex items-center gap-2 overflow-hidden">
                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-xs shrink-0">{currentUser.email.charAt(0).toUpperCase()}</div>
                                <div className="overflow-hidden w-20">
                                    <p className="text-sm font-medium text-slate-900 truncate" title={currentUser.email}>{currentUser.email}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold text-xs shrink-0" title={currentUser.email}>{currentUser.email.charAt(0).toUpperCase()}</div>
                        )}
                        <button onClick={handleLogout} className="text-slate-400 hover:text-slate-600" title="Logout"><LogOut size={18} /></button>
                    </div>
                </div>
            </div>

            <main className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>

                {actingUser && (
                    <div className="bg-amber-100 text-amber-900 px-8 py-3 flex justify-between items-center border-b border-amber-200">
                        <div className="flex items-center gap-2 font-medium">
                            <ShieldAlert size={18} />
                            <span>{t('agency.acting_mode')} <strong>{actingUser.profile?.companyName || actingUser.config.companyName || actingUser.email} {actingUser.profile?.vatNumber ? `(${actingUser.profile.vatNumber})` : ''}</strong></span>
                        </div>
                        <button
                            onClick={handleExitClient}
                            className="text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 px-3 py-1.5 rounded-lg font-bold transition"
                        >
                            {t('agency.exit_dashboard')}
                        </button>
                    </div>
                )}

                <div className="p-8 max-w-10xl mx-auto w-full">
                    <div className="flex justify-between items-center mb-8">
                        {view === 'agency' ? (
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    {t('agency.welcome', {
                                        companyName: currentUser.role === 'agency_member'
                                            ? (currentUser.memberProfile?.name || currentUser.email)
                                            : (currentUser.profile?.companyName || currentUser.config.companyName || 'Agency')
                                    })}
                                </h1>
                                <p className="text-slate-500 mt-1">{t('agency.subtitle')}</p>
                            </div>
                        ) : (
                            <h1 className="text-2xl font-bold text-slate-900">
                                {view === 'config' && 'Widget Configuration'}
                                {view === 'analytics' && 'Analytics Dashboard'}
                                {view === 'embed' && 'Integration'}
                                {view === 'admin' && 'System Administration'}
                                {view === 'admin_billing' && 'Billing Overview'}
                                {view === 'admin_coupons' && 'Coupon Management'}
                                {view === 'billing' && 'Billing & Plans'}
                                {view === 'settings' && (currentUser.role === 'admin' ? 'System Settings' : 'Account Settings')}
                                {view === 'appointments' && 'Bookings'}
                                {view === 'client_dashboard' && 'Client Dashboard'}
                            </h1>
                        )}

                        {view === 'config' && (
                            <button onClick={handleSaveChanges} disabled={isSaving || isUploading} className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-medium hover:opacity-80 transition shadow-lg">
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>

                    {view === 'agency' && (currentUser?.role === 'agency' || currentUser?.role === 'agency_member') && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">{t('agency.stats.clients')}</p>
                                    <div className="flex items-end gap-2">
                                        <span className="text-3xl font-bold text-slate-900">
                                            {agencyClients.filter(c => currentUser.role === 'agency' || (currentUser.role === 'agency_member' && c.assignedAgentIds?.includes(currentUser.id))).length}
                                        </span>
                                        <span className="text-sm text-slate-400 mb-1">{t('agency.stats.slots', { count: agencyClients.length, limit: PLAN_LIMITS[currentUser.plan]?.clients || 0 })}</span>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">{t('agency.stats.plan_status')}</p>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full">{t('agency.stats.active')}</span>
                                    </div>
                                </div>
                                <div
                                    className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm cursor-pointer hover:border-blue-300 transition group"
                                    onClick={() => setView('documents')}
                                >
                                    <p className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2">
                                        {t('agency.stats.pending_review')} {pendingDocsCount > 0 && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            {pendingDocsCount > 0 ? (
                                                <span className="text-3xl font-bold text-slate-900">{pendingDocsCount}</span>
                                            ) : (
                                                <span className="text-3xl font-bold text-slate-300">0</span>
                                            )}
                                            <span className="text-sm text-slate-400 block">{t('agency.stats.new_docs')}</span>
                                        </div>
                                        <div className={`p-3 rounded-xl transition ${pendingDocsCount > 0 ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-100' : 'bg-slate-50 text-slate-400'}`}>
                                            <Inbox size={24} />
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">{t('agency.stats.new_client')}</p>
                                        <p className="text-sm text-slate-400">{t('agency.stats.add_account')}</p>
                                    </div>
                                    {currentUser.role === 'agency' && (
                                        <button
                                            onClick={() => setShowAddClientModal(true)}
                                            disabled={agencyClients.length >= (PLAN_LIMITS[currentUser.plan]?.clients || 5)}
                                            className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg"
                                        >
                                            <Plus size={24} />
                                        </button>
                                    )}
                                </div>
                                <div
                                    className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm cursor-pointer hover:border-red-300 transition group"
                                    onClick={() => { setDocumentsFilter('unread_notes'); setView('documents'); }}
                                >
                                    <p className="text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2">
                                        {t('agency.stats.unread_notes')} {unreadNotesCount > 0 && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            {unreadNotesCount > 0 ? (
                                                <span className="text-3xl font-bold text-slate-900">{unreadNotesCount}</span>
                                            ) : (
                                                <span className="text-3xl font-bold text-slate-300">0</span>
                                            )}
                                            <span className="text-sm text-slate-400 block">{t('agency.stats.unread_notes_label')}</span>
                                        </div>
                                        <div className={`p-3 rounded-xl transition ${unreadNotesCount > 0 ? 'bg-red-50 text-red-600 group-hover:bg-red-100' : 'bg-slate-50 text-slate-400'}`}>
                                            <MessageSquare size={24} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Unread Notes Widget - Added as separate row or 4th item if grid allows, but grid-cols-3 is tight. Let's add it to the grid. */}
                            {/* Actually, let's keep it in the grid. Maybe change to grid-cols-2 md:grid-cols-4 or just append. */}

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                                {/* Moved the existing 3 widgets above into this grid layout if possible, or just add a new row. 
                                    The previous block was: <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> 
                                    Let's just add the new widget into the existing list.
                                */}
                            </div>
                            {/* WAIT, I am inside {view === 'agency' ... <div className="space-y-6"> <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> ... </div> */}




                            {/* Let's try to update the className of the existing grid container first in a separate replace/chunk? 
                                No, I can't reach line 925 easily from here without big context. 
                                Let's just add the interaction logic for Documents View first.
                            */}


                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-6 border-b border-slate-100 bg-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
                                    <h2 className="font-bold text-slate-800 flex items-center gap-2"><Users size={20} /> {t('agency.clients_list.title')}</h2>
                                    <div className="flex items-center gap-3 w-full md:w-auto">
                                        {(currentUser.plan === PlanType.ADVANCED || currentUser.plan === PlanType.AGENCY || currentUser.plan === 'premium' as PlanType || (currentUser.role === 'agency_member' && currentUser.agencyPlan === 'premium' as PlanType)) && (
                                            <>
                                                <button
                                                    onClick={handleRecalculateAttention}
                                                    disabled={isRecalculatingAttention}
                                                    className="bg-white border text-xs border-slate-200 text-slate-700 px-3 py-2 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition disabled:opacity-50"
                                                    title="Recalculate Attention Scores"
                                                >
                                                    <RefreshCw size={14} className={isRecalculatingAttention ? 'animate-spin' : ''} />
                                                    <span className="hidden sm:inline">Recalculate Scores</span>
                                                </button>
                                                <button
                                                    onClick={handleTestAttentionEmail}
                                                    disabled={isSendingTestEmail}
                                                    className="bg-white border text-xs border-slate-200 text-slate-700 px-3 py-2 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition disabled:opacity-50"
                                                    title="Send Test Attention Email to your firm"
                                                >
                                                    <Mail size={14} className={isSendingTestEmail ? 'animate-pulse' : ''} />
                                                    <span className="hidden xl:inline">Test Attention Email</span>
                                                </button>
                                            </>
                                        )}
                                        <div className="relative w-full md:w-64">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                            <input
                                                type="text"
                                                placeholder={t('agency.clients_list.search_placeholder')}
                                                value={clientSearchTerm}
                                                onChange={(e) => setClientSearchTerm(e.target.value)}
                                                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-black transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-50 text-slate-500">
                                            <tr>
                                                <th className="px-6 py-4">{t('agency.clients_list.company_email')}</th>
                                                <th className="px-6 py-4">{t('agency.clients_list.vat_number')}</th>
                                                <th className="px-6 py-4">{t('agency.clients_list.status')}</th>
                                                {(currentUser.plan === PlanType.ADVANCED || currentUser.plan === PlanType.AGENCY || currentUser.plan === 'premium' as PlanType || (currentUser.role === 'agency_member' && currentUser.agencyPlan === 'premium' as PlanType)) && (
                                                    <th className="px-6 py-4 text-center cursor-pointer hover:bg-slate-100 transition whitespace-nowrap" onClick={() => setClientSort(clientSort === 'attention' ? 'name' : 'attention')} title="Click to sort by attention score">
                                                        <div className="flex items-center justify-center gap-1">
                                                            Attention
                                                            {clientSort === 'attention' && <ArrowDown size={14} />}
                                                        </div>
                                                    </th>
                                                )}
                                                {currentUser.config?.ai_features?.document_ai_enabled && <th className="px-6 py-4">AI Advisor</th>}

                                                <th className="px-6 py-4">{t('agency.clients_list.created')}</th>
                                                <th className="px-6 py-4 text-right">{t('agency.clients_list.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {agencyClients.length === 0 ? (
                                                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400">{t('agency.clients_list.no_clients')}</td></tr>
                                            ) : (
                                                agencyClients.filter(c => {
                                                    const term = clientSearchTerm.toLowerCase();
                                                    if (!term) return true;
                                                    return (c.profile?.companyName || c.config.companyName || '').toLowerCase().includes(term) ||
                                                        (c.profile?.vatNumber || '').toLowerCase().includes(term) ||
                                                        c.email.toLowerCase().includes(term);
                                                })
                                                    .sort((a, b) => {
                                                        if (clientSort === 'attention') {
                                                            // For Attention, lower score is worse (needs more attention), so sort ascending by default
                                                            const scoreA = a.attentionIndex ?? 100;
                                                            const scoreB = b.attentionIndex ?? 100;
                                                            return scoreA - scoreB;
                                                        } else {
                                                            // Default sort by name
                                                            const nameA = (a.profile?.companyName || a.config.companyName || a.email).toLowerCase();
                                                            const nameB = (b.profile?.companyName || b.config.companyName || b.email).toLowerCase();
                                                            return nameA.localeCompare(nameB);
                                                        }
                                                    })
                                                    // .filter(c => currentUser.role === 'agency' || (currentUser.role === 'agency_member' && c.assignedAgentIds?.includes(currentUser.id))) // Handled by API
                                                    .map(client => (
                                                        <tr key={client.id} className="hover:bg-slate-50">
                                                            <td className="px-6 py-4">
                                                                <div className="font-bold text-slate-900">{client.profile?.companyName || client.config.companyName || 'Unnamed Company'}</div>
                                                                <div className="text-slate-500">{client.email}</div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className="font-mono text-slate-600">{client.profile?.vatNumber || '-'}</span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => handleToggleClientStatus(client)}
                                                                        className={`w-10 h-6 rounded-full transition-colors relative ${client.subscriptionStatus === 'active' ? 'bg-green-500' : 'bg-slate-300'}`}
                                                                    >
                                                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${client.subscriptionStatus === 'active' ? 'left-5' : 'left-1'}`}></span>
                                                                    </button>
                                                                    <span className="text-xs font-medium text-slate-500 capitalize">{client.subscriptionStatus || 'active'}</span>
                                                                </div>
                                                            </td>
                                                            {(currentUser.plan === PlanType.ADVANCED || currentUser.plan === PlanType.AGENCY || currentUser.plan === 'premium' as PlanType || (currentUser.role === 'agency_member' && currentUser.agencyPlan === 'premium' as PlanType)) && (
                                                                <td className="px-6 py-4 text-center">
                                                                    <div
                                                                        className="inline-flex items-center justify-center p-2 rounded-full cursor-help hover:bg-slate-100 transition-colors"
                                                                        title={client.attentionReasons?.length ? client.attentionReasons.join('\n') : "Overall score: " + (client.attentionIndex ?? 100)}
                                                                    >
                                                                        {(client.attentionIndex ?? 100) >= 70 ? (
                                                                            <CheckCircle className="text-green-500" size={20} />
                                                                        ) : (client.attentionIndex ?? 100) >= 40 ? (
                                                                            <AlertTriangle className="text-amber-500" size={20} />
                                                                        ) : (
                                                                            <AlertCircle className="text-red-500" size={20} />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            )}
                                                            {currentUser.config?.ai_features?.document_ai_enabled && (
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="flex flex-col">
                                                                            <div className="flex items-center gap-1 font-bold text-slate-700 text-xs">
                                                                                <Bot size={14} className="text-purple-600" />
                                                                                <span>Advisor</span>
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleToggleClientFeature(client, 'document_ai_enabled', true)}
                                                                            className={`w-10 h-5 rounded-full transition-colors relative ${client.config.ai_features?.document_ai_enabled ? 'bg-purple-600' : 'bg-slate-200'}`}
                                                                        >
                                                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${client.config.ai_features?.document_ai_enabled ? 'left-5' : 'left-1'}`}></span>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            )}
                                                            <td className="px-6 py-4 text-slate-500">
                                                                {formatDate(client.createdAt || '', currentUser?.dateFormat)}
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <button
                                                                    onClick={() => handleManageClient(client)}
                                                                    className="bg-black text-white px-4 py-2 rounded-lg text-xs font-bold hover:opacity-80 transition"
                                                                >
                                                                    {t('agency.clients_list.manage')}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleResendInvitation(client)}
                                                                    className="bg-blue-50 text-blue-600 p-2 rounded-lg ml-2 hover:bg-blue-100 transition"
                                                                    title={t('agency.clients_list.resend_invitation', 'Resend Invitation')}
                                                                >
                                                                    <Mail size={16} />
                                                                </button>
                                                                {currentUser.role === 'agency' && (
                                                                    <button
                                                                        onClick={() => { setClientToDelete(client); setShowDeleteClientModal(true); }}
                                                                        className="bg-red-50 text-red-600 p-2 rounded-lg ml-2 hover:bg-red-100 transition"
                                                                        title="Delete Client"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {view === 'client_dashboard' && <ClientDashboard user={targetUser as User} onViewDocuments={() => setView('documents')} onRefreshDocuments={refreshAiContext} />}
                    {view === 'obligations' && <ClientObligationsView user={targetUser as User} />}

                    {view === 'config' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-6">

                                <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-8 -mt-8 z-0"></div>
                                    <div className="relative z-10">
                                        <div className="flex items-center justify-between mb-6">
                                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                                <TrendingUp size={20} className="text-blue-600" /> Performance Overview
                                            </h2>
                                            <select
                                                value={statsDateRange}
                                                onChange={(e) => setStatsDateRange(e.target.value as any)}
                                                className="text-xs font-semibold px-2 py-1 bg-white border border-slate-200 rounded-lg shadow-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                            >
                                                <option value="all">All Time</option>
                                                <option value="today">Today</option>
                                                <option value="yesterday">Yesterday</option>
                                                <option value="week">Past Week</option>
                                                <option value="month">Past Month</option>
                                                <option value="year">Past Year</option>
                                            </select>
                                        </div>


                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                                <p className="text-xs font-bold text-slate-500 uppercase mb-1">Widget Opens</p>
                                                <div className="flex items-center gap-2">
                                                    <Eye size={18} className="text-blue-500" />
                                                    <span className="text-2xl font-bold text-slate-900">{quickStats.opens}</span>
                                                </div>
                                            </div>
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col justify-center">
                                                <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                                                    {targetUser.config.trackExternalLinks ? 'Message Interactions' : 'Messages'}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <Mail size={18} className="text-purple-500" />
                                                    <span className="text-2xl font-bold text-slate-900">{quickStats.emails}</span>
                                                </div>
                                                {targetUser.config.trackExternalLinks && (
                                                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 space-y-1">
                                                        <div className="flex justify-between">
                                                            <span>Widget:</span>
                                                            <span className="font-bold text-slate-700">{quickStats.widgetEmails}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span>Website:</span>
                                                            <span className="font-bold text-slate-700">{quickStats.externalEmails}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col justify-center">
                                                <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                                                    Chat Interactions
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <MessageCircle size={18} className="text-blue-500" />
                                                    <span className="text-2xl font-bold text-slate-900">{quickStats.chatClicks}</span>
                                                </div>
                                                <div className="mt-1 text-xs text-slate-400">Viber, Messenger, WhatsApp</div>
                                            </div>
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col justify-center">
                                                <p className="text-xs font-bold text-slate-500 uppercase mb-1">
                                                    {targetUser.config.trackExternalLinks ? 'Phone Interactions' : 'Phone Clicks'}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <MousePointer size={18} className="text-green-500" />
                                                    <span className="text-2xl font-bold text-slate-900">{quickStats.clicks}</span>
                                                </div>
                                                {targetUser.config.trackExternalLinks && (
                                                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 space-y-1">
                                                        <div className="flex justify-between">
                                                            <span>Widget:</span>
                                                            <span className="font-bold text-slate-700">{quickStats.widgetClicks}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span>Website:</span>
                                                            <span className="font-bold text-slate-700">{quickStats.externalClicks}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                                <p className="text-xs font-bold text-slate-500 uppercase mb-1">Bookings</p>
                                                <div className="flex items-center gap-2">
                                                    <Calendar size={18} className="text-orange-500" />
                                                    <span className="text-2xl font-bold text-slate-900">{quickStats.appointments}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setView('analytics')}
                                            className="mt-4 w-full py-2 text-sm font-bold text-blue-600 hover:bg-blue-50 rounded-lg transition flex items-center justify-center gap-2"
                                        >
                                            View Full Report <ArrowRight size={16} />
                                        </button>
                                    </div>
                                </section>

                                <section className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm ${targetUser.plan === PlanType.FREE ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                                    <div className="flex justify-between items-start mb-4">
                                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                            <BarChart size={20} /> Analytics Reporting
                                        </h2>
                                        {targetUser.plan === PlanType.FREE && <span className="text-xs text-orange-500 font-bold flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full"><Lock size={10} /> Premium</span>}
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-slate-500">{targetUser.config.enableEmailReports ? 'Enabled' : 'Disabled'}</span>
                                            <button
                                                onClick={() => updateConfig('enableEmailReports', !targetUser.config.enableEmailReports)}
                                                className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.enableEmailReports ? 'bg-black' : 'bg-slate-200'}`}
                                            >
                                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.enableEmailReports ? 'left-7' : 'left-1'}`}></span>
                                            </button>
                                        </div>
                                    </div>

                                    {targetUser.config.enableEmailReports && (
                                        <div className="space-y-4 animate-fadeIn">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Report Frequency</label>
                                                <div className="grid grid-cols-3 gap-2">
                                                    {['daily', 'weekly', 'monthly'].map((freq) => (
                                                        <button
                                                            key={freq}
                                                            onClick={() => updateConfig('reportFrequency', freq)}
                                                            className={`py-2 px-3 rounded-lg text-sm font-bold border-2 transition capitalize ${targetUser.config.reportFrequency === freq ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-100 hover:border-slate-200 text-slate-500'}`}
                                                        >
                                                            {freq}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-sm text-slate-600">
                                                <p className="flex items-start gap-2">
                                                    <Mail size={16} className="shrink-0 mt-0.5" />
                                                    Reports will be sent to: <strong>{targetUser.config.contactEmail}</strong>
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleSendTestReport}
                                                disabled={isSendingReport}
                                                className="w-full bg-white border border-slate-300 text-slate-700 font-bold py-2.5 rounded-lg hover:bg-slate-50 transition flex items-center justify-center gap-2"
                                            >
                                                {isSendingReport ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                                                Send Test Report Now
                                            </button>
                                        </div>
                                    )}

                                    <div className="pt-4 border-t border-slate-100 mt-4">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><ExternalLink size={16} /> Track External Links</p>
                                                <p className="text-xs text-slate-500 mt-1">Capture clicks on <code>tel:</code>, <code>form-submit, </code> and <code>mailto:</code> links across your website for internal analytics use.</p>
                                            </div>
                                            <button
                                                onClick={() => updateConfig('trackExternalLinks', !targetUser.config.trackExternalLinks)}
                                                className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.trackExternalLinks ? 'bg-black' : 'bg-slate-200'}`}
                                            >
                                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.trackExternalLinks ? 'left-7' : 'left-1'}`}></span>
                                            </button>
                                        </div>
                                    </div>
                                </section>


                                <div className="flex items-center gap-6 mb-8 border-b border-slate-200">
                                    <button
                                        onClick={() => setSettingsTab('general')}
                                        className={`pb-4 text-sm font-bold transition ${settingsTab === 'general' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        General Settings
                                    </button>
                                    {(currentUser.role === 'agency' || currentUser.role === 'agency_member') && (PLAN_LIMITS[currentUser.plan]?.members || 0) > 0 && (
                                        <button
                                            onClick={() => setSettingsTab('team')}
                                            className={`pb-4 text-sm font-bold transition ${settingsTab === 'team' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
                                        >
                                            Team Members
                                        </button>
                                    )}
                                </div>

                                {settingsTab === 'general' && (
                                    <div className="space-y-6">
                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm"><h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Settings size={20} /> General Info</h2><div className="space-y-4"><div><label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label><input type="text" value={targetUser.config.companyName} onChange={(e) => updateConfig('companyName', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" /></div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Logo</label>
                                                <input type="file" accept="image/*" onChange={handleLogoUpload} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50" disabled={isLogoUploading} />
                                                {isLogoUploading && <p className="text-xs text-blue-600 mt-2 flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> Processing logo...</p>}
                                            </div>
                                            <div><label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label><input type="text" value={targetUser.config.websiteUrl} onChange={(e) => updateConfig('websiteUrl', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" /></div><div><label className="block text-sm font-medium text-slate-700 mb-1">Description (Header Text)</label><textarea value={targetUser.config.description} onChange={(e) => updateConfig('description', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 h-20 outline-none" placeholder="e.g. Hi, nice to see you here!"></textarea></div></div>

                                            <div className={`pt-4 border-t border-slate-100 mt-2 ${targetUser.plan === PlanType.FREE ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                                                <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                    <Link size={16} /> Custom Home Button
                                                    {targetUser.plan === PlanType.FREE && <span className="text-xs text-orange-500 font-bold flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full ml-auto"><Lock size={10} /> Premium</span>}
                                                </h3>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">Label</label>
                                                        <input type="text" value={targetUser.config.customHomeBtnLabel || ''} onChange={(e) => updateConfig('customHomeBtnLabel', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" placeholder="Learn More" />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">URL</label>
                                                        <input type="text" value={targetUser.config.customHomeBtnUrl || ''} onChange={(e) => updateConfig('customHomeBtnUrl', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" placeholder="https://example.com" />
                                                    </div>
                                                </div>
                                            </div>
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                                <Layout size={20} /> Widget Appearance
                                            </h2>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 uppercase">Alignment</label>
                                                    <select
                                                        value={targetUser.config.widgetAlignment || 'right'}
                                                        onChange={(e) => updateConfig('widgetAlignment', e.target.value)}
                                                        className="w-full border p-2 rounded-lg mt-1 bg-white"
                                                    >
                                                        <option value="right">Bottom Right</option>
                                                        <option value="left">Bottom Left</option>
                                                        <option value="center">Bottom Center</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-bold text-slate-500 uppercase">Theme</label>
                                                    <select
                                                        value={targetUser.config.theme || 'light'}
                                                        onChange={(e) => updateConfig('theme', e.target.value)}
                                                        className="w-full border p-2 rounded-lg mt-1 bg-white"
                                                    >
                                                        <option value="light">Light Mode</option>
                                                        <option value="dark">Dark Mode</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-xs font-bold text-slate-500 uppercase">Text Color (Optional)</label>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <input
                                                            type="color"
                                                            value={targetUser.config.themeTextColor || (targetUser.config.theme === 'dark' ? '#ffffff' : '#0f172a')}
                                                            onChange={(e) => updateConfig('themeTextColor', e.target.value)}
                                                            className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1"
                                                        />
                                                        <span className="text-xs text-slate-500 font-mono">{targetUser.config.themeTextColor || (targetUser.config.theme === 'dark' ? '#ffffff' : '#0f172a')}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Advanced Colors */}
                                            <div className="mt-6 pt-4 border-t border-slate-100">
                                                <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                    <Palette size={16} /> Advanced Colors
                                                </h3>

                                                {/* Widget Bubble (Closed) */}
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">Widget Bubble Bg</label>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <input type="color" value={targetUser.config.widgetClosedBgColor || targetUser.config.themeColor || '#000000'} onChange={(e) => updateConfig('widgetClosedBgColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                            <span className="text-xs text-slate-500 font-mono">{targetUser.config.widgetClosedBgColor || 'Brand'}</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">Widget Icon Color</label>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <input type="color" value={targetUser.config.widgetClosedIconColor || '#ffffff'} onChange={(e) => updateConfig('widgetClosedIconColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                            <span className="text-xs text-slate-500 font-mono">{targetUser.config.widgetClosedIconColor || 'Auto'}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Main Contact Button */}
                                                <div className="grid grid-cols-2 gap-4 mb-4">
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">Contact Btn Bg</label>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <input type="color" value={targetUser.config.mainContactBtnBgColor || targetUser.config.themeColor || '#000000'} onChange={(e) => updateConfig('mainContactBtnBgColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                            <span className="text-xs text-slate-500 font-mono">{targetUser.config.mainContactBtnBgColor || 'Brand'}</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-slate-500 uppercase">Contact Btn Text</label>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <input type="color" value={targetUser.config.mainContactBtnTextColor || '#ffffff'} onChange={(e) => updateConfig('mainContactBtnTextColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                            <span className="text-xs text-slate-500 font-mono">{targetUser.config.mainContactBtnTextColor || 'Auto'}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Custom Home Button (Only if URL set) */}
                                                {targetUser.config.customHomeBtnUrl && (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-xs font-bold text-slate-500 uppercase">Home Btn Bg</label>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <input type="color" value={targetUser.config.customHomeBtnBgColor || targetUser.config.themeColor || '#000000'} onChange={(e) => updateConfig('customHomeBtnBgColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                                <span className="text-xs text-slate-500 font-mono">{targetUser.config.customHomeBtnBgColor || 'Brand'}</span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-bold text-slate-500 uppercase">Home Btn Text</label>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <input type="color" value={targetUser.config.customHomeBtnTextColor || '#ffffff'} onChange={(e) => updateConfig('customHomeBtnTextColor', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1" />
                                                                <span className="text-xs text-slate-500 font-mono">{targetUser.config.customHomeBtnTextColor || 'Auto'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </section>

                                        {/* Integrations Section */}
                                        <section className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm ${(targetUser.plan === PlanType.FREE || targetUser.plan === PlanType.LIGHT) ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                                <BarChart size={20} /> Integrations
                                            </h2>
                                            {(targetUser.plan === PlanType.FREE || targetUser.plan === PlanType.LIGHT) && <div className="mb-4"><span className="text-xs text-orange-500 font-bold flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full w-fit"><Lock size={10} /> Premium (Advanced)</span></div>}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">Google Analytics ID (GA4)</label>
                                                    <input
                                                        type="text"
                                                        value={targetUser.config.googleAnalyticsId || ''}
                                                        onChange={(e) => updateConfig('googleAnalyticsId', e.target.value)}
                                                        className="w-full border border-slate-300 rounded-lg p-2.5 outline-none"
                                                        placeholder="G-XXXXXXXXXX"
                                                    />
                                                    <p className="text-xs text-slate-400 mt-1">
                                                        Enter your Measurement ID to track widget events in your Google Analytics dashboard in case you don't already load gtag script on your website.
                                                    </p>
                                                </div>
                                            </div>
                                        </section>

                                        <section className={`bg-white p-6 rounded-2xl border border-slate-200 shadow-sm ${(targetUser.plan === PlanType.FREE || targetUser.plan === PlanType.LIGHT) ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <div className="flex justify-between items-start mb-4">
                                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                                    <Calendar size={20} /> Booking & Appointments
                                                </h2>
                                                {(targetUser.plan === PlanType.FREE || targetUser.plan === PlanType.LIGHT) && <span className="text-xs text-orange-500 font-bold flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full ml-2"><Lock size={10} /> Premium (Advanced)</span>}
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-slate-500">{targetUser.config.enableBooking ? 'Enabled' : 'Disabled'}</span>
                                                    <button
                                                        onClick={() => updateConfig('enableBooking', !targetUser.config.enableBooking)}
                                                        className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.enableBooking ? 'bg-black' : 'bg-slate-200'}`}
                                                    >
                                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.enableBooking ? 'left-7' : 'left-1'}`}></span>
                                                    </button>
                                                </div>
                                            </div>
                                            {targetUser.config.enableBooking && (
                                                <div className="space-y-4 animate-fadeIn">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 mb-1">Home Button Label</label>
                                                        <input
                                                            type="text"
                                                            value={targetUser.config.labelBookingBtn || 'Book Appointment'}
                                                            onChange={(e) => updateConfig('labelBookingBtn', e.target.value)}
                                                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none"
                                                        />
                                                    </div>



                                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-2">
                                                        <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                            <CalendarCheck size={16} /> Schedule Settings
                                                        </h3>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Slot Duration</label>
                                                                <select
                                                                    value={targetUser.config.appointmentDuration || 60}
                                                                    onChange={(e) => updateConfig('appointmentDuration', parseInt(e.target.value))}
                                                                    className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white text-sm"
                                                                >
                                                                    <option value="15">15 Minutes</option>
                                                                    <option value="30">30 Minutes</option>
                                                                    <option value="45">45 Minutes</option>
                                                                    <option value="60">1 Hour</option>
                                                                    <option value="90">1.5 Hours</option>
                                                                    <option value="120">2 Hours</option>
                                                                </select>
                                                            </div>

                                                            <div className="grid grid-cols-2 gap-3">
                                                                <div>
                                                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Opening Time</label>
                                                                    <input
                                                                        type="time"
                                                                        value={targetUser.config.businessHoursStart || '09:00'}
                                                                        onChange={(e) => updateConfig('businessHoursStart', e.target.value)}
                                                                        className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white text-sm"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Closing Time</label>
                                                                    <input
                                                                        type="time"
                                                                        value={targetUser.config.businessHoursEnd || '17:00'}
                                                                        onChange={(e) => updateConfig('businessHoursEnd', e.target.value)}
                                                                        className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white text-sm"
                                                                    />
                                                                </div>
                                                            </div>

                                                            {(targetUser.config.availableDays || []).some(d => d === 0 || d === 6) && (
                                                                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-200">
                                                                    <div>
                                                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Weekend Opening</label>
                                                                        <input
                                                                            type="time"
                                                                            value={targetUser.config.businessHoursStartWeekend || '10:00'}
                                                                            onChange={(e) => updateConfig('businessHoursStartWeekend', e.target.value)}
                                                                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white text-sm"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Weekend Closing</label>
                                                                        <input
                                                                            type="time"
                                                                            value={targetUser.config.businessHoursEndWeekend || '15:00'}
                                                                            onChange={(e) => updateConfig('businessHoursEndWeekend', e.target.value)}
                                                                            className="w-full border border-slate-300 rounded-lg p-2.5 outline-none bg-white text-sm"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div>
                                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Available Days</label>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {weekDays.map(day => {
                                                                        const isSelected = (targetUser.config.availableDays || [1, 2, 3, 4, 5]).includes(day.id);
                                                                        return (
                                                                            <button
                                                                                key={day.id}
                                                                                onClick={() => toggleAvailableDay(day.id)}
                                                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition border ${isSelected
                                                                                    ? 'bg-black text-white border-black'
                                                                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                                                                    }`}
                                                                            >
                                                                                {day.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-2">
                                                        <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                            <Tag size={16} /> Custom Labels
                                                        </h3>
                                                        <div className="space-y-4">
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="col-span-2"><label className="text-xs font-bold text-slate-500 uppercase">Main Title</label><input type="text" value={targetUser.config.labelBookNowTitle || 'Book Now'} onChange={(e) => updateConfig('labelBookNowTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2"><h4 className="text-xs font-bold text-slate-400 uppercase">Step Headers</h4></div>
                                                                <div><label className="text-xs text-slate-500">Select Type</label><input type="text" value={targetUser.config.labelSelectType || 'Select an appointment type:'} onChange={(e) => updateConfig('labelSelectType', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Select Date</label><input type="text" value={targetUser.config.labelSelectDate || 'Select a date:'} onChange={(e) => updateConfig('labelSelectDate', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Select Time</label><input type="text" value={targetUser.config.labelSelectTime || 'Select a time for'} onChange={(e) => updateConfig('labelSelectTime', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Select Location</label><input type="text" value={targetUser.config.labelSelectLocation || 'Select Location'} onChange={(e) => updateConfig('labelSelectLocation', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Choose Location</label><input type="text" value={targetUser.config.labelChooseLocation || 'Choose Location'} onChange={(e) => updateConfig('labelChooseLocation', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2 flex justify-between items-center">
                                                                    <h4 className="text-xs font-bold text-slate-400 uppercase">Consultation Type</h4>
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <span className="text-[10px] font-bold text-slate-500 uppercase">{targetUser.config.enableConsultation !== false ? 'Active' : 'Disabled'}</span>
                                                                        <input type="checkbox" checked={targetUser.config.enableConsultation !== false} onChange={(e) => updateConfig('enableConsultation', e.target.checked)} className="accent-black" />
                                                                    </label>
                                                                </div>
                                                                <div><label className="text-xs text-slate-500">Label</label><input type="text" value={targetUser.config.labelConsultation || 'Online Consultation'} onChange={(e) => updateConfig('labelConsultation', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Description</label><input type="text" value={targetUser.config.descConsultation || 'Video call discussion'} onChange={(e) => updateConfig('descConsultation', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2 flex justify-between items-center">
                                                                    <h4 className="text-xs font-bold text-slate-400 uppercase">Session Type</h4>
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <span className="text-[10px] font-bold text-slate-500 uppercase">{targetUser.config.enableSession !== false ? 'Active' : 'Disabled'}</span>
                                                                        <input type="checkbox" checked={targetUser.config.enableSession !== false} onChange={(e) => updateConfig('enableSession', e.target.checked)} className="accent-black" />
                                                                    </label>
                                                                </div>
                                                                <div><label className="text-xs text-slate-500">Label</label><input type="text" value={targetUser.config.labelSession || 'Online Session'} onChange={(e) => updateConfig('labelSession', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Description</label><input type="text" value={targetUser.config.descSession || 'Full 1 hour session'} onChange={(e) => updateConfig('descSession', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2 flex justify-between items-center">
                                                                    <h4 className="text-xs font-bold text-slate-400 uppercase">On-site Type</h4>
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <span className="text-[10px] font-bold text-slate-500 uppercase">{targetUser.config.enableOnsite !== false ? 'Active' : 'Disabled'}</span>
                                                                        <input type="checkbox" checked={targetUser.config.enableOnsite !== false} onChange={(e) => updateConfig('enableOnsite', e.target.checked)} className="accent-black" />
                                                                    </label>
                                                                </div>
                                                                <div><label className="text-xs text-slate-500">Label</label><input type="text" value={targetUser.config.labelOnsite || 'Appointment On-site'} onChange={(e) => updateConfig('labelOnsite', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Description</label><input type="text" value={targetUser.config.descOnsite || 'Visit us at our office'} onChange={(e) => updateConfig('descOnsite', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2"><h4 className="text-xs font-bold text-slate-400 uppercase">Form Labels</h4></div>
                                                                <div><label className="text-xs text-slate-500">Confirm Button</label><input type="text" value={targetUser.config.labelConfirmBookingBtn || 'Confirm Booking'} onChange={(e) => updateConfig('labelConfirmBookingBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Name Label</label><input type="text" value={targetUser.config.labelFormName || 'Your Name'} onChange={(e) => updateConfig('labelFormName', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Email Label</label><input type="text" value={targetUser.config.labelFormEmail || 'Your Email'} onChange={(e) => updateConfig('labelFormEmail', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Phone Label</label><input type="text" value={targetUser.config.labelFormPhone || 'Your Phone'} onChange={(e) => updateConfig('labelFormPhone', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Message Label</label><input type="text" value={targetUser.config.labelFormMessage || 'Message (Optional)'} onChange={(e) => updateConfig('labelFormMessage', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Error Message</label><input type="text" value={targetUser.config.labelFillAllFields || 'Please fill in all fields'} onChange={(e) => updateConfig('labelFillAllFields', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Callback Link</label><input type="text" value={targetUser.config.labelRequestCallback || 'Request a call back'} onChange={(e) => updateConfig('labelRequestCallback', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Callback Name</label><input type="text" value={targetUser.config.labelCallbackName || 'Your Name'} onChange={(e) => updateConfig('labelCallbackName', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Callback Phone</label><input type="text" value={targetUser.config.labelCallbackPhone || 'Phone Number'} onChange={(e) => updateConfig('labelCallbackPhone', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>

                                                                <div className="col-span-2 mt-2"><h4 className="text-xs font-bold text-slate-400 uppercase">Success Screen</h4></div>
                                                                <div><label className="text-xs text-slate-500">Title</label><input type="text" value={targetUser.config.labelSuccessTitle || 'Booking Confirmed!'} onChange={(e) => updateConfig('labelSuccessTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Back Button</label><input type="text" value={targetUser.config.labelBackToHome || 'Back to Home'} onChange={(e) => updateConfig('labelBackToHome', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">Confirmation Msg</label><input type="text" value={targetUser.config.labelSuccessMessage || 'We have sent a confirmation email to'} onChange={(e) => updateConfig('labelSuccessMessage', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                                <div><label className="text-xs text-slate-500">See You On</label><input type="text" value={targetUser.config.labelSeeYouOn || 'See you on'} onChange={(e) => updateConfig('labelSeeYouOn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm bg-white" /></div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4 pt-2 mt-2">
                                                        <div>
                                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Button Color</label>
                                                            <div className="flex items-center gap-3">
                                                                <input
                                                                    type="color"
                                                                    value={targetUser.config.bookingBtnColor || '#ffffff'}
                                                                    onChange={(e) => updateConfig('bookingBtnColor', e.target.value)}
                                                                    className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1"
                                                                />
                                                                <span className="text-xs text-slate-500 font-mono">{targetUser.config.bookingBtnColor || '#ffffff'}</span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Text Color</label>
                                                            <div className="flex items-center gap-3">
                                                                <input
                                                                    type="color"
                                                                    value={targetUser.config.bookingBtnTextColor || '#1e293b'}
                                                                    onChange={(e) => updateConfig('bookingBtnTextColor', e.target.value)}
                                                                    className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-1"
                                                                />
                                                                <span className="text-xs text-slate-500 font-mono">{targetUser.config.bookingBtnTextColor || '#1e293b'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm"><h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><CreditCard size={20} /> Contact & Reviews</h2>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="col-span-2"><label className="text-sm font-medium block">Email</label><input type="text" value={targetUser.config.contactEmail} onChange={(e) => updateConfig('contactEmail', e.target.value)} className="w-full border p-2 rounded-lg" /></div>

                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Label</label><input type="text" value={targetUser.config.phone1Label || 'Phone 1'} onChange={(e) => updateConfig('phone1Label', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>
                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Number</label><input type="text" value={targetUser.config.phone1} onChange={(e) => updateConfig('phone1', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>

                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Label</label><input type="text" value={targetUser.config.phone2Label || 'Phone 2'} onChange={(e) => updateConfig('phone2Label', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>
                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Number</label><input type="text" value={targetUser.config.phone2} onChange={(e) => updateConfig('phone2', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>

                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Label</label><input type="text" value={targetUser.config.phone3Label || 'Phone 3'} onChange={(e) => updateConfig('phone3Label', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>
                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Number</label><input type="text" value={targetUser.config.phone3} onChange={(e) => updateConfig('phone3', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>

                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Label</label><input type="text" value={targetUser.config.mobileLabel || 'Mobile'} onChange={(e) => updateConfig('mobileLabel', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>
                                                <div><label className="text-xs font-bold text-slate-500 uppercase">Number</label><input type="text" value={targetUser.config.mobile} onChange={(e) => updateConfig('mobile', e.target.value)} className="w-full border p-2 rounded-lg mt-1" /></div>

                                                <div className="col-span-2 pt-4 border-t border-slate-100 mt-2">
                                                    <h3 className="text-sm font-bold text-slate-800 mb-2 uppercase tracking-wider flex items-center gap-2">
                                                        <MessageSquare size={16} /> Messaging Apps
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="col-span-2">
                                                            <label className="text-xs font-bold text-slate-500 uppercase">Viber Number</label>
                                                            <input
                                                                type="text"
                                                                value={targetUser.config.contactViber || ''}
                                                                onChange={(e) => updateConfig('contactViber', e.target.value)}
                                                                className="w-full border p-2 rounded-lg mt-1"
                                                                placeholder="+306912345678"
                                                            />
                                                            <p className="text-xs text-slate-400 mt-1">
                                                                Enter your full phone number with country code (e.g., +30...)
                                                            </p>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="text-xs font-bold text-slate-500 uppercase">Messenger Username</label>
                                                            <input
                                                                type="text"
                                                                value={targetUser.config.contactMessenger || ''}
                                                                onChange={(e) => updateConfig('contactMessenger', e.target.value)}
                                                                className="w-full border p-2 rounded-lg mt-1"
                                                                placeholder="yourusername"
                                                            />
                                                            <p className="text-xs text-slate-400 mt-1">
                                                                Enter your Facebook Messenger username.
                                                            </p>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="text-xs font-bold text-slate-500 uppercase">WhatsApp Number</label>
                                                            <input
                                                                type="text"
                                                                value={targetUser.config.contactWhatsapp || ''}
                                                                onChange={(e) => updateConfig('contactWhatsapp', e.target.value)}
                                                                className="w-full border p-2 rounded-lg mt-1"
                                                                placeholder="306912345678"
                                                            />
                                                            <p className="text-xs text-slate-400 mt-1">
                                                                Enter your phone number with country code, no + or spaces.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>



                                                <div><label className="text-sm font-medium block">Google Rating (0-5)</label><input type="number" step="0.1" min="0" max="5" value={targetUser.config.googleRating || 5.0} onChange={(e) => updateConfig('googleRating', parseFloat(e.target.value))} className="w-full border p-2 rounded-lg" /></div>
                                                <div><label className="text-sm font-medium block">Review Count</label><input type="number" value={targetUser.config.googleReviewCount || 0} onChange={(e) => updateConfig('googleReviewCount', parseInt(e.target.value))} className="w-full border p-2 rounded-lg" /></div>

                                                <div className="col-span-2">
                                                    <label className="text-sm font-medium block">Google Reviews URL</label>
                                                    <input type="text" value={targetUser.config.googleReviewsUrl || ''} onChange={(e) => updateConfig('googleReviewsUrl', e.target.value)} className="w-full border p-2 rounded-lg" placeholder="https://maps.google.com/..." />
                                                </div>

                                            </div></section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <div className="flex justify-between items-start mb-4">
                                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                                    <PlayCircle size={20} /> Welcome Message
                                                </h2>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-slate-500">{targetUser.config.enableWelcomeMessage !== false ? 'Enabled' : 'Disabled'}</span>
                                                    <button
                                                        onClick={() => updateConfig('enableWelcomeMessage', targetUser.config.enableWelcomeMessage === false)}
                                                        className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.enableWelcomeMessage !== false ? 'bg-black' : 'bg-slate-200'}`}
                                                    >
                                                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.enableWelcomeMessage !== false ? 'left-7' : 'left-1'}`}></span>
                                                    </button>
                                                </div>
                                            </div>
                                            {targetUser.config.enableWelcomeMessage !== false && (
                                                <div className="space-y-4 animate-fadeIn">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 mb-2">Video Source Type</label>
                                                        <div className="flex gap-4">
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input
                                                                    type="radio"
                                                                    name="videoType"
                                                                    value="upload"
                                                                    checked={targetUser.config.welcomeVideoType === 'upload'}
                                                                    onChange={() => updateConfig('welcomeVideoType', 'upload')}
                                                                />
                                                                <span className="text-sm">Custom MP4 URL</span>
                                                            </label>
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input
                                                                    type="radio"
                                                                    name="videoType"
                                                                    value="youtube"
                                                                    checked={targetUser.config.welcomeVideoType === 'youtube'}
                                                                    onChange={() => updateConfig('welcomeVideoType', 'youtube')}
                                                                />
                                                                <span className="text-sm">YouTube / Shorts</span>
                                                            </label>

                                                        </div>
                                                    </div>

                                                    {targetUser.config.welcomeVideoType === 'upload' && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 mb-1">MP4 Video URL</label>
                                                            <input
                                                                type="text"
                                                                value={targetUser.config.welcomeVideoUrl || ''}
                                                                onChange={(e) => updateConfig('welcomeVideoUrl', e.target.value)}
                                                                className="w-full border border-slate-300 rounded-lg p-2.5 outline-none"
                                                                placeholder="https://your-website.com/videos/intro.mp4"
                                                            />
                                                            <p className="text-xs text-slate-400 mt-1">Direct link to an .mp4 file.</p>
                                                        </div>
                                                    )}

                                                    {targetUser.config.welcomeVideoType === 'youtube' && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 mb-1">YouTube URL</label>
                                                            <input
                                                                type="text"
                                                                value={targetUser.config.welcomeVideoUrl || ''}
                                                                onChange={(e) => updateConfig('welcomeVideoUrl', e.target.value)}
                                                                className="w-full border border-slate-300 rounded-lg p-2.5 outline-none"
                                                                placeholder="https://youtube.com/shorts/..."
                                                            />
                                                        </div>
                                                    )}



                                                    <div className="pt-4 border-t border-slate-100 mt-2">
                                                        <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                            <Image size={16} /> Carousel Images
                                                        </h3>
                                                        <p className="text-xs text-slate-500 mb-3">Displayed if no video is provided. Auto-slides every few seconds.</p>

                                                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                                                            {(targetUser.config.welcomeImages || []).map((img, idx) => (
                                                                <div key={idx} className="relative aspect-[9/16] bg-slate-100 rounded-lg overflow-hidden border border-slate-200 group">
                                                                    <img src={img.url} className="w-full h-full object-cover" alt={`Slide ${idx + 1}`} />
                                                                    <button
                                                                        onClick={() => removeWelcomeImage(idx)}
                                                                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition shadow-sm"
                                                                    >
                                                                        <X size={12} />
                                                                    </button>
                                                                    <div className="absolute bottom-0 left-0 w-full p-1 bg-black/50">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Link"
                                                                            value={img.link || ''}
                                                                            onChange={(e) => updateWelcomeImageLink(idx, e.target.value)}
                                                                            className="w-full text-[10px] p-1 rounded border-none outline-none bg-white/90 text-black"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            {(targetUser.config.welcomeImages || []).length < 5 && (
                                                                <label className={`aspect-[9/16] border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition ${isImageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                                                    {isImageUploading ? <Loader2 className="animate-spin text-slate-400" size={20} /> : <Plus className="text-slate-400" size={24} />}
                                                                    <input type="file" accept="image/*" multiple onChange={handleWelcomeImageUpload} className="hidden" />
                                                                </label>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={imageUrlInput}
                                                                onChange={(e) => setImageUrlInput(e.target.value)}
                                                                className="flex-1 border border-slate-300 rounded-lg p-2 text-sm outline-none"
                                                                placeholder="Or paste image URL here..."
                                                            />
                                                            <button
                                                                onClick={handleAddImageUrl}
                                                                className="px-4 py-2 bg-black text-white text-xs font-bold rounded-lg hover:opacity-80"
                                                            >
                                                                Add
                                                            </button>
                                                        </div>
                                                        <p className="text-xs text-slate-400 mt-2">Max 5 images. Aspect ratio 9:16 recommended.</p>
                                                    </div>
                                                </div>
                                            )}
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                                <Share2 size={20} /> Social Media
                                            </h2>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">Facebook URL</label>
                                                    <input type="text" value={targetUser.config.socialFacebook || ''} onChange={(e) => updateConfig('socialFacebook', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" placeholder="https://facebook.com/..." />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">Instagram URL</label>
                                                    <input type="text" value={targetUser.config.socialInstagram || ''} onChange={(e) => updateConfig('socialInstagram', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" placeholder="https://instagram.com/..." />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">YouTube URL</label>
                                                    <input type="text" value={targetUser.config.socialYoutube || ''} onChange={(e) => updateConfig('socialYoutube', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" placeholder="https://youtube.com/..." />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn URL</label>
                                                    <input type="text" value={targetUser.config.socialLinkedin || ''} onChange={(e) => updateConfig('socialLinkedin', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" placeholder="https://linkedin.com/..." />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">TikTok URL</label>
                                                    <input type="text" value={targetUser.config.socialTiktok || ''} onChange={(e) => updateConfig('socialTiktok', e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 outline-none" placeholder="https://tiktok.com/@..." />
                                                </div>
                                            </div>
                                        </section>


                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <div className="flex justify-between items-center mb-4">
                                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                                    <MapPin size={20} /> Locations (Max 3)
                                                </h2>
                                                <button onClick={addAddress} className="text-blue-600 font-bold flex items-center gap-1 text-sm"><Plus size={16} /> Add</button>
                                            </div>
                                            <div className="space-y-4">
                                                {(targetUser.config.addresses || []).map((addr) => (
                                                    <div key={addr.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative group">
                                                        <button onClick={() => removeAddress(addr.id)} className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="text-xs font-bold text-slate-400 uppercase">Label</label>
                                                                <input
                                                                    type="text"
                                                                    value={addr.label}
                                                                    onChange={(e) => updateAddress(addr.id, 'label', e.target.value)}
                                                                    className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm"
                                                                    placeholder="e.g. Headquarters"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-bold text-slate-400 uppercase">Address</label>
                                                                <input
                                                                    type="text"
                                                                    value={addr.address}
                                                                    onChange={(e) => updateAddress(addr.id, 'address', e.target.value)}
                                                                    className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm"
                                                                    placeholder="e.g. 123 Main St"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-bold text-slate-400 uppercase">Google Maps URL</label>
                                                                <input
                                                                    type="text"
                                                                    value={addr.mapUrl}
                                                                    onChange={(e) => updateAddress(addr.id, 'mapUrl', e.target.value)}
                                                                    className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-sm"
                                                                    placeholder="https://maps.google.com/..."
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {(targetUser.config.addresses || []).length === 0 && (
                                                    <p className="text-sm text-slate-400 italic">No locations added yet.</p>
                                                )}
                                            </div>
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Layout size={20} /> Appearance</h2>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4">
                                                    <input type="color" value={targetUser.config.themeColor} onChange={(e) => updateConfig('themeColor', e.target.value)} className="w-12 h-12 rounded cursor-pointer border-0 p-0" />
                                                    <div><p className="font-medium text-slate-900">Brand Color</p><p className="text-sm text-slate-500">{targetUser.config.themeColor}</p></div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1">Widget Position</label>
                                                    <div className="grid grid-cols-3 gap-2">
                                                        <button
                                                            onClick={() => updateConfig('widgetAlignment', 'left')}
                                                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition ${targetUser.config.widgetAlignment === 'left' ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-100 hover:border-slate-200 text-slate-500'}`}
                                                        >
                                                            <AlignLeft size={20} className="mb-1" />
                                                            <span className="text-xs font-bold">Left</span>
                                                        </button>
                                                        <button
                                                            onClick={() => updateConfig('widgetAlignment', 'center')}
                                                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition ${targetUser.config.widgetAlignment === 'center' ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-100 hover:border-slate-200 text-slate-500'}`}
                                                        >
                                                            <AlignCenter size={20} className="mb-1" />
                                                            <span className="text-xs font-bold">Center</span>
                                                        </button>
                                                        <button
                                                            onClick={() => updateConfig('widgetAlignment', 'right')}
                                                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition ${(!targetUser.config.widgetAlignment || targetUser.config.widgetAlignment === 'right') ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-slate-100 hover:border-slate-200 text-slate-500'}`}
                                                        >
                                                            <AlignRight size={20} className="mb-1" />
                                                            <span className="text-xs font-bold">Right</span>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="pt-4 border-t border-slate-100 mt-2">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700">Widget Branding</label>
                                                            <p className="text-xs text-slate-500">Show "Powered by AccountingFlow" link</p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {targetUser.plan === PlanType.FREE && <span className="text-xs text-orange-500 font-bold flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full"><Lock size={10} /> Premium</span>}
                                                            <button
                                                                disabled={targetUser.plan === PlanType.FREE}
                                                                onClick={() => updateConfig('showBranding', !targetUser.config.showBranding)}
                                                                className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.showBranding ? 'bg-black' : 'bg-slate-200'} ${targetUser.plan === PlanType.FREE ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.showBranding ? 'left-7' : 'left-1'}`}></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="pt-4 border-t border-slate-100 mt-2">
                                                    <h3 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider flex items-center gap-2">
                                                        <Type size={16} /> Text Labels
                                                    </h3>
                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="col-span-2"><h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Navigation Tabs</h4></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Home Tab</label><input type="text" value={targetUser.config.tabLabelHome || 'Home'} onChange={(e) => updateConfig('tabLabelHome', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Welcome Tab</label><input type="text" value={targetUser.config.tabLabelWelcome || 'Welcome'} onChange={(e) => updateConfig('tabLabelWelcome', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Contact Tab</label><input type="text" value={targetUser.config.tabLabelContact || 'Contact'} onChange={(e) => updateConfig('tabLabelContact', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Call Us Tab</label><input type="text" value={targetUser.config.tabLabelCallus || 'Call Us'} onChange={(e) => updateConfig('tabLabelCallus', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Help Tab</label><input type="text" value={targetUser.config.tabLabelHelp || 'Help'} onChange={(e) => updateConfig('tabLabelHelp', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>

                                                            <div className="col-span-2 mt-2"><h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Buttons & Headings</h4></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Main Contact Button</label><input type="text" value={targetUser.config.labelMainContactBtn || 'Contact us'} onChange={(e) => updateConfig('labelMainContactBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">FAQ Title</label><input type="text" value={targetUser.config.labelFaqTitle || 'FAQ'} onChange={(e) => updateConfig('labelFaqTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Quick Answers</label><input type="text" value={targetUser.config.labelQuickAnswers || 'Quick answers'} onChange={(e) => updateConfig('labelQuickAnswers', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Show All Questions</label><input type="text" value={targetUser.config.labelShowAllQuestions || 'Show all questions'} onChange={(e) => updateConfig('labelShowAllQuestions', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Contact Title</label><input type="text" value={targetUser.config.labelContactTitle || 'Contact us'} onChange={(e) => updateConfig('labelContactTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Leave Message</label><input type="text" value={targetUser.config.labelLeaveMessageBtn || 'Leave a message'} onChange={(e) => updateConfig('labelLeaveMessageBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Msg Details Title</label><input type="text" value={targetUser.config.labelMessageTitle || 'Details'} onChange={(e) => updateConfig('labelMessageTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Msg Phone Label</label><input type="text" value={targetUser.config.labelMessagePhone || 'Phone Number (Optional)'} onChange={(e) => updateConfig('labelMessagePhone', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Msg Send Button</label><input type="text" value={targetUser.config.labelMessageSendBtn || 'Send'} onChange={(e) => updateConfig('labelMessageSendBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Call Us Button</label><input type="text" value={targetUser.config.labelCallUsBtn || 'Call Us'} onChange={(e) => updateConfig('labelCallUsBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Visit Us Button</label><input type="text" value={targetUser.config.labelVisitUsBtn || 'Visit Us'} onChange={(e) => updateConfig('labelVisitUsBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>

                                                            <div><label className="text-xs font-bold text-slate-500">Call Us Page Title</label><input type="text" value={targetUser.config.labelCallUsTitle || 'Call us'} onChange={(e) => updateConfig('labelCallUsTitle', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Viber Button</label><input type="text" value={targetUser.config.labelViberBtn || 'Viber Message'} onChange={(e) => updateConfig('labelViberBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                            <div><label className="text-xs font-bold text-slate-500">Messenger Button</label><input type="text" value={targetUser.config.labelMessengerBtn || 'Messenger'} onChange={(e) => updateConfig('labelMessengerBtn', e.target.value)} className="w-full border p-2 rounded-lg mt-1 text-sm" /></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>

                                        <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <div className="flex justify-between items-center mb-4">
                                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Code size={20} /> FAQs (Manual)</h2>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-2 mr-4">
                                                        <span className="text-sm text-slate-500">{targetUser.config.enableFaq !== false ? 'Enabled' : 'Disabled'}</span>
                                                        <button
                                                            onClick={() => updateConfig('enableFaq', targetUser.config.enableFaq === false)}
                                                            className={`w-12 h-6 rounded-full transition-colors relative ${targetUser.config.enableFaq !== false ? 'bg-black' : 'bg-slate-200'}`}
                                                        >
                                                            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${targetUser.config.enableFaq !== false ? 'left-7' : 'left-1'}`}></span>
                                                        </button>
                                                    </div>
                                                    <button onClick={addFaq} className="text-blue-600 font-bold flex items-center gap-1"><Plus size={16} /> Add</button>
                                                </div>
                                            </div>
                                            {targetUser.config.enableFaq !== false && (
                                                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                                                    {targetUser.config.faqs?.map((faq, index) => (
                                                        <div key={faq.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                                            <div className="flex justify-between mb-2">
                                                                <input type="text" value={faq.question} onChange={(e) => updateFaq(faq.id, 'question', e.target.value)} className="bg-transparent font-semibold w-full outline-none" />
                                                                <div className="flex gap-2 items-center">
                                                                    <div className="flex bg-white rounded-lg border border-slate-200 mr-2">
                                                                        <button
                                                                            onClick={() => moveFaq(index, 'up')}
                                                                            disabled={index === 0}
                                                                            className={`p-1 ${index === 0 ? 'text-slate-300' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
                                                                            title="Move Up"
                                                                        >
                                                                            <ArrowUp size={14} />
                                                                        </button>
                                                                        <div className="w-px bg-slate-200 my-1"></div>
                                                                        <button
                                                                            onClick={() => moveFaq(index, 'down')}
                                                                            disabled={index === targetUser.config.faqs.length - 1}
                                                                            className={`p-1 ${index === targetUser.config.faqs.length - 1 ? 'text-slate-300' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
                                                                            title="Move Down"
                                                                        >
                                                                            <ArrowDown size={14} />
                                                                        </button>
                                                                    </div>
                                                                    <button onClick={() => updateFaq(faq.id, 'isVisible', !faq.isVisible)} className={faq.isVisible ? 'text-green-600' : 'text-slate-400'}><CheckCircle size={14} /></button>
                                                                    <button onClick={() => removeFaq(faq.id)} className="text-red-500"><Trash2 size={14} /></button>
                                                                </div>
                                                            </div>
                                                            <textarea value={faq.answer} onChange={(e) => updateFaq(faq.id, 'answer', e.target.value)} className="w-full bg-transparent text-sm resize-none outline-none" rows={2} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </section>
                                    </div>
                                )}
                            </div>

                            {/* Team Tab Content */}
                            {settingsTab === 'team' && (currentUser.role === 'agency' || currentUser.role === 'agency_member') && (PLAN_LIMITS[currentUser.plan]?.members || 0) > 0 && (
                                <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
                                    <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                                        <div>
                                            <h2 className="font-bold text-slate-800 flex items-center gap-2"><Users size={20} /> Team Members</h2>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {agencyMembers.length} / {PLAN_LIMITS[currentUser.plan]?.members || 0} Slots Used
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setShowAddMemberModal(true)}
                                            className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-50 transition shadow-sm flex items-center gap-2"
                                        >
                                            <Plus size={16} /> Add Member
                                        </button>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-slate-500">
                                                <tr>
                                                    <th className="px-6 py-4">Name</th>
                                                    <th className="px-6 py-4">Email</th>
                                                    <th className="px-6 py-4">Phone</th>
                                                    <th className="px-6 py-4 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {agencyMembers.length === 0 ? (
                                                    <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-400">No team members yet.</td></tr>
                                                ) : (
                                                    agencyMembers.map(member => (
                                                        <tr key={member.id} className="hover:bg-slate-50">
                                                            <td className="px-6 py-4 font-bold text-slate-900">{member.memberProfile?.name || '-'}</td>
                                                            <td className="px-6 py-4 text-slate-600">{member.email}</td>
                                                            <td className="px-6 py-4 text-slate-600">{member.memberProfile?.phone || '-'}</td>
                                                            <td className="px-6 py-4 text-right">
                                                                <button onClick={() => handleDeleteMember(member.id)} className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition"><Trash2 size={16} /></button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            )}

                            {
                                currentUser.role !== 'admin' && null // Preview disabled
                            }
                        </div >

                    )
                    }




                    {/* {view === 'analytics' && <AnalyticsDashboard userId={targetUser.id} dateFormat={targetUser.dateFormat} timezone={targetUser.timezone} />} */}
                    {/* {view === 'appointments' && <AppointmentsView user={targetUser} />} */}

                    {
                        view === 'admin' && currentUser.role === 'admin' && (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fadeIn">
                                <div className="p-6 border-b border-slate-100 flex justify-between bg-slate-50">
                                    <h2 className="font-bold text-slate-800">
                                        Admin User Management ({allUsers?.length || 0})
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        <select
                                            value={selectedAgencyFilter}
                                            onChange={(e) => setSelectedAgencyFilter(e.target.value)}
                                            className="bg-white border border-slate-200 rounded-lg py-2 px-3 text-sm outline-none focus:ring-1 focus:ring-black cursor-pointer"
                                        >
                                            <option value="all">All Users</option>
                                            {allUsers.filter(u => u.role === 'agency').map(agency => (
                                                <option key={agency.id} value={agency.id}>
                                                    {agency.profile?.companyName || agency.config.companyName || agency.email}
                                                </option>
                                            ))}
                                        </select>
                                        <button onClick={() => setShowAddUserModal(true)} className="bg-black text-white px-4 py-2 rounded-lg text-sm hover:opacity-80 transition">Create User</button>
                                    </div>
                                </div>

                                {!allUsers || allUsers.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500">
                                        No users found.
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                                                <tr>
                                                    <th className="px-6 py-4">User</th>
                                                    <th className="px-6 py-4">Plan</th>
                                                    <th className="px-6 py-4">Status</th>
                                                    <th className="px-6 py-4">Expires</th>
                                                    <th className="px-6 py-4 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {allUsers
                                                    .filter(u => selectedAgencyFilter === 'all' || u.agencyId === selectedAgencyFilter || u.id === selectedAgencyFilter)
                                                    .map(u => (
                                                        <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                                                            <td className="px-6 py-4">
                                                                <div className="font-medium text-slate-900">{u.profile?.companyName || u.config.companyName || 'Unnamed Company'}</div>
                                                                <div className="text-slate-500 text-xs mt-0.5">{u.email}</div>
                                                                {u.role === 'agency' && <span className="inline-block mt-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold tracking-wide">AGENCY</span>}
                                                                {u.agencyId && (
                                                                    <div className="text-[10px] text-blue-600 font-bold mt-1.5 flex items-center gap-1 bg-blue-50 w-fit px-1.5 py-0.5 rounded">
                                                                        <Briefcase size={10} />
                                                                        <span>
                                                                            {allUsers.find(a => a.id === u.agencyId)?.profile?.companyName ||
                                                                                allUsers.find(a => a.id === u.agencyId)?.config.companyName ||
                                                                                allUsers.find(a => a.id === u.agencyId)?.email ||
                                                                                'Unknown Agency'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {u.usedCoupon && <div className="mt-1 flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded w-fit"><Tag size={10} /><span className="font-bold">{u.usedCoupon}</span></div>}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <select
                                                                    value={u.plan}
                                                                    onChange={(e) => handleChangePlan(u.id, e.target.value as any)}
                                                                    className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-black cursor-pointer"
                                                                >
                                                                    <option value="free">Free</option>
                                                                    <option value="light">Starter</option>
                                                                    <option value="advanced">Advanced</option>
                                                                    <option value="agency">Premium</option>
                                                                </select>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <select
                                                                    value={u.subscriptionStatus}
                                                                    onChange={(e) => handleStatusChange(u.id, e.target.value as any)}
                                                                    className={`px-2 py-1 w-24 rounded-full text-xs border-0 cursor-pointer outline-none font-bold text-center appearance-none ${u.subscriptionStatus === 'active' ? 'bg-green-100 text-green-700' : u.subscriptionStatus === 'expired' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}
                                                                >
                                                                    <option value="active">Active</option>
                                                                    <option value="expired">Expired</option>
                                                                    <option value="cancelled">Cancelled</option>
                                                                </select>
                                                            </td>
                                                            <td className="px-6 py-4 text-slate-600 font-mono text-xs">{u.subscriptionEnd || '-'}</td>
                                                            <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                                <button onClick={() => handleManageClient(u)} className="bg-black text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:opacity-80 transition flex items-center gap-1">
                                                                    Manage
                                                                </button>
                                                                <button onClick={() => handleExtendSubscription(u.id)} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition" title="Extend"><Calendar size={16} /></button>
                                                                <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition" title="Delete"><Trash2 size={16} /></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )
                    }

                    {view === 'admin_billing' && <AdminBillingView />}
                    {view === 'admin_coupons' && <AdminCoupons />}

                    {
                        view === 'settings' && <SystemSettingsView user={targetUser} onUserUpdate={async () => {
                            const updated = await api.getUser(targetUser.id);
                            if (actingUser) {
                                setActingUser(updated);
                                setAgencyClients(prev => prev.map(c => c.id === updated.id ? updated : c));
                            } else {
                                setCurrentUser(updated);
                            }
                        }} onAgencyClientsUpdate={async () => {
                            if (currentUser?.role === 'agency') {
                                const clients = await api.getAgencyClients(currentUser.id);
                                setAgencyClients(clients);
                            }
                        }} />
                    }

                    {
                        view === 'documents' && (
                            ((currentUser.role === 'agency' || currentUser.role === 'agency_member') && !actingUser)
                                ? <DocumentsManagementView user={currentUser} initialFilter={documentsFilter} />
                                : <ClientDocumentsView user={targetUser} agencyUser={actingUser ? currentUser : undefined} />
                        )
                    }

                    {view === 'browse_docs' && <FileBrowserView currentUser={currentUser} agencyClients={agencyClients} />}

                    {view === 'bi_financial' && <BIFinancialView />}

                    {
                        view === 'embed' && (
                            <div className="max-w-3xl mx-auto space-y-8">
                                <div className="bg-white p-8 rounded-2xl border border-slate-200 text-center">
                                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Embed Widget</h2>
                                    <p className="text-slate-500 mb-6">Copy and paste this code snippet into your website's body.</p>
                                    <div className="bg-slate-900 rounded-xl p-4 text-left relative group">
                                        <pre className="text-sm text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap">{`<script src="https://accountingflow.web.app/widget.js" data-id="${targetUser.id}" async></script>`}</pre>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(`<script src="https:/accountingflow.web.app/widget.js" data-id="${targetUser.id}" async></script>`);
                                                alert("Code copied to clipboard!");
                                            }}
                                            className="absolute top-2 right-2 p-2 bg-slate-800 text-slate-400 rounded-lg hover:text-white transition-colors"
                                            title="Copy to clipboard"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200">
                                        <h3 className="font-bold mb-2">License Key</h3>
                                        <div className="bg-slate-50 p-3 rounded font-mono text-sm text-slate-600">{targetUser.id}</div>
                                    </div>
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200">
                                        <h3 className="font-bold mb-2">Allowed Domain</h3>
                                        <div className="bg-slate-50 p-3 rounded font-mono text-sm text-slate-600">{targetUser.config.websiteUrl || 'Any'}</div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 mt-6">
                                    <h3 className="font-bold mb-2 flex items-center gap-2 text-slate-800"><ExternalLink size={20} /> Trigger via Link</h3>
                                    <p className="text-sm text-slate-600 mb-4">You can open the widget automatically by adding <code>#open-widget</code> to any link on your website.</p>
                                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                                        <code className="text-sm text-blue-600">&lt;a href="#open-widget"&gt;Contact Us&lt;/a&gt;</code>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {view === 'billing' && <BillingView user={targetUser} onUpgrade={initUpgrade} onCancel={handleCancelSubscription} />}
                </div >
            </main >

            {pendingUpgrade && (
                <PlanConfirmationModal
                    isOpen={!!pendingUpgrade}
                    onClose={() => setPendingUpgrade(null)}
                    onConfirm={handleConfirmUpgrade}
                    isLoading={isProcessingPayment}
                    planName={pendingUpgrade.plan}
                    price={pendingUpgrade.price}
                    cycle={pendingUpgrade.cycle}
                    isUpgrade={pendingUpgrade.plan !== currentUser?.plan}
                    taxRate={pendingUpgrade.taxRate}
                />
            )
            }

            <SubscriptionCancelModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleConfirmCancel}
                isLoading={isProcessingPayment}
                endDate={formatDate(targetUser?.subscriptionEnd || new Date().toISOString(), targetUser?.dateFormat)}
            />

            {showPaymentModal && selectedPlanDetails && <PaymentModal plan={selectedPlanDetails.id} cycle={selectedPlanDetails.cycle} price={selectedPlanDetails.price} onClose={() => setShowPaymentModal(false)} onConfirm={processPayment} isProcessing={isProcessingPayment} />}

            {showAddUserModal && <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"><div className="bg-white rounded-2xl w-full max-w-md p-6"><h3 className="font-bold mb-4">Create User</h3><div className="space-y-4"><input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="w-full border p-2 rounded" placeholder="Email" /><select value={newUserPlan} onChange={(e) => setNewUserPlan(e.target.value as any)} className="w-full border p-2 rounded"><option value="free">Free</option><option value="light">Starter</option><option value="advanced">Advanced</option><option value="agency">Premium</option></select><div className="flex justify-end gap-2"><button onClick={() => setShowAddUserModal(false)}>Cancel</button><button onClick={handleCreateUser} className="bg-black text-white px-4 py-2 rounded">Create</button></div></div></div></div>}

            {
                showAddClientModal && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                            {/* Header */}
                            <div className="bg-slate-50 p-6 border-b border-slate-100">
                                <h3 className="text-lg font-bold text-slate-900">{t('agency.modals.add_client.title')}</h3>
                                <p className="text-sm text-slate-500">{t('agency.modals.add_client.slots_used', { count: agencyClients.length, limit: PLAN_LIMITS[currentUser.plan]?.clients || 0 })}</p>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('agency.clients_list.vat_number')}</label>
                                    <input
                                        type="text"
                                        value={newClientVat}
                                        onChange={(e) => setNewClientVat(e.target.value)}
                                        className="w-full border p-3 rounded-lg mt-1 outline-none focus:ring-2 focus:ring-black font-medium text-slate-900"
                                        placeholder="EL123456789"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('agency.modals.add_client.company_name')}</label>
                                    <input
                                        type="text"
                                        value={newClientCompanyName}
                                        onChange={(e) => setNewClientCompanyName(e.target.value)}
                                        className="w-full border p-3 rounded-lg mt-1 outline-none focus:ring-2 focus:ring-black font-medium text-slate-900"
                                        placeholder={t('agency.modals.add_client.company_placeholder')}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('agency.modals.add_client.responsible_person')}</label>
                                    <input
                                        type="text"
                                        value={newClientResponsiblePerson}
                                        onChange={(e) => setNewClientResponsiblePerson(e.target.value)}
                                        className="w-full border p-3 rounded-lg mt-1 outline-none focus:ring-2 focus:ring-black font-medium text-slate-900"
                                        placeholder={t('agency.modals.add_client.responsible_placeholder')}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">{t('agency.modals.add_client.client_email')}</label>
                                    <input
                                        type="email"
                                        value={newUserEmail}
                                        onChange={(e) => setNewUserEmail(e.target.value)}
                                        className="w-full border p-3 rounded-lg mt-1 outline-none focus:ring-2 focus:ring-black font-medium text-slate-900"
                                        placeholder={t('agency.modals.add_client.email_placeholder')}
                                    />
                                    <div className="mt-3 bg-blue-50 text-blue-800 p-3 rounded-xl text-xs flex items-start gap-2">
                                        <Key size={14} className="mt-0.5 shrink-0" />
                                        <span>{t('agency.modals.add_client.temp_password_info')}</span>
                                    </div>

                                    {/* Send Invitation Checkbox */}
                                    <div className="mt-4 flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="sendInvitation"
                                            checked={sendInvitation}
                                            onChange={(e) => setSendInvitation(e.target.checked)}
                                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        />
                                        <label htmlFor="sendInvitation" className="text-sm font-medium text-slate-700 cursor-pointer">
                                            {t('agency.modals.add_client.send_invitation')}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
                                <button
                                    onClick={() => { setShowAddClientModal(false); setNewUserEmail(''); setNewClientCompanyName(''); setNewClientResponsiblePerson(''); setNewClientVat(''); }}
                                    className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition"
                                >
                                    {t('agency.modals.add_client.cancel')}
                                </button>
                                <button
                                    onClick={handleCreateClient}
                                    disabled={isCreatingUser}
                                    className="px-6 py-2 bg-black text-white font-bold rounded-lg hover:opacity-80 flex items-center gap-2 transition"
                                >
                                    {isCreatingUser && <Loader2 className="animate-spin" size={16} />}
                                    {isCreatingUser ? t('agency.modals.add_client.creating') : t('agency.modals.add_client.create')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Client Confirmation Modal */}
            {
                showAddMemberModal && (
                    <AddMemberModal
                        isOpen={showAddMemberModal}
                        onClose={() => setShowAddMemberModal(false)}
                        onConfirm={handleCreateMember}
                        limit={PLAN_LIMITS[currentUser.plan]?.members || 0}
                        count={agencyMembers.length}
                    />
                )
            }

            {/* Delete Client Confirmation Modal */}
            {
                showDeleteClientModal && clientToDelete && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                            {/* Header */}
                            <div className="bg-red-50 p-6 border-b border-red-100 flex items-center gap-3">
                                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-red-600 shadow-sm"><AlertTriangle size={20} /></div>
                                <div>
                                    <h3 className="text-lg font-bold text-red-900">{t('agency.modals.delete_client.title', 'Delete Client?')}</h3>
                                    <p className="text-sm text-red-700 font-medium">{t('agency.modals.delete_client.subtitle', 'This action cannot be undone.')}</p>
                                </div>
                            </div>

                            <div className="p-6">
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    {t('agency.modals.delete_client.message_pre', 'Are you sure you want to delete ')}
                                    <strong>{clientToDelete.config?.companyName || clientToDelete.email}</strong>
                                    {t('agency.modals.delete_client.message_post', '?')}
                                </p>
                                <p className="text-slate-600 text-sm leading-relaxed mt-2">
                                    {t('agency.modals.delete_client.warning_pre', 'All documents, notes, and communication history will be ')}
                                    <strong>{t('agency.modals.delete_client.warning_bold', 'permanently deleted')}</strong>
                                    {t('agency.modals.delete_client.warning_post', '. You will not be able to restore this data.')}
                                </p>
                            </div>

                            {/* Footer */}
                            <div className="p-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
                                <button
                                    onClick={() => setShowDeleteClientModal(false)}
                                    className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 rounded-lg transition"
                                >
                                    {t('agency.modals.delete_client.cancel', 'Cancel')}
                                </button>
                                <button
                                    onClick={() => { confirmDeleteClient(); }}
                                    className="px-6 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 flex items-center gap-2 transition"
                                >
                                    <Trash2 size={16} /> {t('agency.modals.delete_client.confirm', 'Delete Forever')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }


            {/* Resend Invitation Confirmation Modal */}
            {showResendInviteModal && clientToResend && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden scale-100 transition-all">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg text-slate-800">{t('agency.modals.resend_invitation.title', 'Resend Invitation')}</h3>
                            <button onClick={() => setShowResendInviteModal(false)} className="text-slate-400 hover:text-slate-600 transition">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-slate-600 text-sm leading-relaxed">
                                {t('agency.modals.resend_invitation.message_pre', 'Are you sure you want to resend the invitation to ')}
                                <strong>{clientToResend.email}</strong>
                                {t('agency.modals.resend_invitation.message_post', '?')}
                            </p>
                            <p className="text-slate-500 text-xs mt-3 bg-blue-50 p-3 rounded-lg border border-blue-100 flex gap-2">
                                <span className="text-blue-500 mt-0.5"><AlertCircle size={14} /></span>
                                <span>{t('agency.modals.resend_invitation.warning', 'This will reset their password and email them new credentials.')}</span>
                            </p>
                        </div>
                        <div className="p-4 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
                            <button
                                onClick={() => setShowResendInviteModal(false)}
                                className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-200 rounded-lg transition"
                                disabled={isResending}
                            >
                                {t('agency.modals.resend_invitation.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={confirmResendInvitation}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-sm flex items-center gap-2"
                                disabled={isResending}
                            >
                                {isResending ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        {t('agency.modals.resend_invitation.sending', 'Sending...')}
                                    </>
                                ) : (
                                    <>
                                        <Mail size={16} />
                                        {t('agency.modals.resend_invitation.confirm', 'Resend Invitation')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {
                createdClientCredentials && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
                        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl text-center">
                            <div className="bg-green-50 p-6 border-b border-green-100 flex flex-col items-center">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-3">
                                    <Check size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-green-900">Client Created!</h3>
                            </div>
                            <div className="p-8 space-y-6">
                                <div className="text-left bg-slate-50 p-4 rounded-xl border border-slate-100">
                                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Email</p>
                                    <p className="text-lg font-bold text-slate-900">{createdClientCredentials.email}</p>
                                </div>
                                <div className="text-left bg-slate-50 p-4 rounded-xl border border-slate-100">
                                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Temporary Password</p>
                                    <div className="flex items-center justify-between">
                                        <p className="text-lg font-mono font-bold text-slate-900">{createdClientCredentials.password}</p>
                                        <button
                                            onClick={() => { navigator.clipboard.writeText(createdClientCredentials.password); alert("Copied!") }}
                                            className="text-blue-600 hover:bg-blue-50 p-2 rounded"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-500">
                                    Please share these credentials with your client safely.
                                </p>
                            </div>
                            <div className="p-4 bg-slate-50 border-t border-slate-100">
                                <button
                                    onClick={() => setCreatedClientCredentials(null)}
                                    className="w-full bg-black text-white py-3 rounded-xl font-bold hover:opacity-80 transition"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* AI Assistant Widget (Global) */}
            {targetUser && (
                <AiAssistant
                    currentUser={currentUser}
                    targetUser={targetUser}
                    agencyName={currentUser.role === 'agency' ? (currentUser.profile?.companyName || currentUser.config?.companyName) : undefined}
                    documents={aiDocuments}
                    documentTypes={aiDocTypes}
                />
            )}

        </div >
    );
}